import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  Container,
  useTheme,
  IconButton,
  Divider,
  TextField,
  Card,
  CardContent,
  Checkbox,
  Alert,
  GlobalStyles,
  Tabs,
  Tab
} from '@mui/material';
import {
  Public as PublicIcon,
  LocationOn as LocationIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  BarChart as BarChartIcon,
  Topic as TopicIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Layers as LayersIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Campaign as CampaignIcon,
  Article as ArticleIcon,
  Info as InfoIcon,
  LocalOffer as LocalOfferIcon,
  ErrorOutline as ErrorOutlineIcon,
  ArrowBack as ArrowBackIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Restore as HistoryIcon,
  Assessment as ImpressionsIcon,
  TrackChanges as SuccessRateIcon,
  FormatListNumbered as QueueIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import {
  industryClusters,
  publications,
  countries
} from '../../data/distributionData';
// matchPublications, aiRankPublications, and getPressRelease are now used in RealTimePublicationScanning component
import { getToken } from '../../utils/tokenHelper';
import { getPressReleases } from '../../services/prApi';
import { matchPublications, getChannels, aiRankPublications, getUserWishlist, saveUserWishlist } from '../../services/distributionApi';
import { countries as allCountriesSource } from '../../data/distributionData';
import { loadStripe } from '@stripe/stripe-js';
import { shouldLoadStripe, getApiBaseUrl } from '../../utils/apiConfig';
import { createApiClient } from '../../utils/axiosConfig';
import { CheckoutForm } from './CheckoutForm';
import { RealTimePublicationScanning } from './RealTimePublicationScanning';
import { calculateItemPrice, SelectionData, PackageData, TIER_PRICING, getTierKey } from './types';

// All types and functions are imported from types.ts
// Helper to calculate domain authority based on tier
const calculateDA = (tier: number): number => {
  const daMap: { [key: number]: number } = {
    1: 93,
    2: 85,
    3: 78,
    4: 70,
    5: 65
  }
  return daMap[tier] || 70
}

// Helper to calculate monthly reach based on tier
const calculateMonthlyReach = (tier: number): number => {
  const reachMap: { [key: number]: number } = {
    1: 100,
    2: 75,
    3: 50,
    4: 30,
    5: 20
  }
  return reachMap[tier] || 20
}

// Helper to generate fallback email from website
const generateEmail = (website: string): string => {
  return `editor@${website}`
}

// All Countries List (alphabetically sorted)
const allCountries = [...allCountriesSource].sort((a, b) =>
  a.localeCompare(b),
);

// Navigation Buttons Component
export const StepNavigation: React.FC<{
  onBack?: () => void;
  onNext?: () => void;
  backDisabled?: boolean;
  nextDisabled?: boolean;
  nextLabel?: string;
}> = ({ onBack, onNext, backDisabled = false, nextDisabled = false, nextLabel = 'Next' }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, px: 2 }}>
      <Button
        onClick={onBack}
        disabled={backDisabled || !onBack}
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        sx={{
          color: '#09273b',
          borderColor: '#09273b',
          borderWidth: '2px',
          textTransform: 'none',
          fontWeight: 600,
          px: 3,
          transition: 'all 0.3s ease',
          '&:hover': {
            borderColor: '#ff5200',
            backgroundColor: '#fff5f0',
            color: '#ff5200',
            borderWidth: '2px',
            transform: 'translateY(-2px)'
          },
          '&:disabled': {
            borderColor: '#e0e0e0',
            color: '#999',
            borderWidth: '1px'
          }
        }}
      >
        Back
      </Button>
      {onNext && (
        <Button
          onClick={onNext}
          disabled={nextDisabled}
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          sx={{
            backgroundColor: '#ff5200',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 'none',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#e64a00',
              boxShadow: 'none',
              transform: 'translateY(-2px)'
            },
            '&:disabled': {
              backgroundColor: '#ccc',
              color: '#999',
              boxShadow: 'none'
            }
          }}
        >
          {nextLabel}
        </Button>
      )}
    </Box>
  );
};

// Helper Functions are imported from types.ts above



// Selection Card Component
const SelectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  badge?: string;
  compact?: boolean;
  multiSelect?: boolean;
  highlightGreen?: boolean; // New prop to highlight in green for tier selection
}> = ({ icon, title, description, selected, onClick, badge, compact, multiSelect = false, highlightGreen = false }) => {
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2.5,
        cursor: 'pointer',
        backgroundColor: selected ? '#fff' : '#fafafa',
        border: selected ? (highlightGreen ? '3px solid #4CAF50' : '3px solid #ff5200') : '1px solid #e0e0e0',
        borderRadius: 2,
        transition: 'all 0.2s ease',
        display: 'flex',
        alignItems: 'center',
        gap: compact ? 1.5 : 2,
        '&:hover': {
          backgroundColor: '#fff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          transform: 'translateX(4px)'
        }
      }}
    >
      <Box
        sx={{
          width: compact ? 36 : 44,
          height: compact ? 36 : 44,
          borderRadius: 2,
          backgroundColor: selected ? '#ff5200' : '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          flexShrink: 0
        }}
      >
        {React.cloneElement(icon as React.ReactElement, {
          sx: { fontSize: compact ? 18 : 22, color: selected ? '#fff' : '#09273b' }
        })}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant={compact ? "body2" : "subtitle1"}
            sx={{ fontWeight: 600, color: '#09273b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {title}
          </Typography>
          {badge && (
            <Chip label={badge} size="small" sx={{ backgroundColor: '#ff5200', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: 18 }} />
          )}
        </Box>
        {!compact && (
          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
            {description}
          </Typography>
        )}
      </Box>
      {multiSelect ? (
        <Box
          sx={{
            width: 20,
            height: 20,
            borderRadius: '4px',
            border: selected ? '2px solid #ff5200' : '2px solid #ccc',
            backgroundColor: selected ? '#ff5200' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            transition: 'all 0.2s ease'
          }}
        >
          {selected && <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />}
        </Box>
      ) : (
        selected && <ArrowForwardIcon sx={{ color: '#ff5200', fontSize: compact ? 16 : 20, flexShrink: 0 }} />
      )}
    </Paper>
  );
};

// Industry Card with Expandable Sub-Clusters
const IndustrySelectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  subClusters?: string[];
  expanded: boolean;
  onToggleExpand: () => void;
  selectedSubClusters: string[];
  onSubClusterToggle: (subCluster: string) => void;
  industryId: string;
}> = ({ icon, title, description, selected, onClick, subClusters, expanded, onToggleExpand, selectedSubClusters, onSubClusterToggle, industryId }) => {
  const hasSubClusters = subClusters && subClusters.length > 0;

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          cursor: 'pointer',
          backgroundColor: selected ? '#fff' : '#fafafa',
          border: selected ? '2px solid #ff5200' : '2px solid transparent',
          borderRadius: 2,
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          '&:hover': {
            backgroundColor: '#fff',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            transform: 'translateX(4px)'
          }
        }}
        onClick={onClick}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            backgroundColor: selected ? '#ff5200' : '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease',
            flexShrink: 0
          }}
        >
          {React.cloneElement(icon as React.ReactElement, {
            sx: { fontSize: 18, color: selected ? '#fff' : '#09273b' }
          })}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: '#09273b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {title}
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {description}
          </Typography>
        </Box>
        {hasSubClusters && (
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpand();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 24,
              height: 24,
              borderRadius: 1,
              backgroundColor: expanded ? '#ff5200' : '#e0e0e0',
              transition: 'all 0.2s ease',
              flexShrink: 0,
              '&:hover': {
                backgroundColor: expanded ? '#e64a00' : '#d0d0d0'
              }
            }}
          >
            {expanded ? (
              <ExpandLessIcon sx={{ fontSize: 16, color: '#fff' }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 16, color: '#666' }} />
            )}
          </Box>
        )}
      </Paper>

      {/* Expanded Sub-Clusters */}
      {expanded && hasSubClusters && (
        <Box sx={{ ml: 5, mt: 1, mb: 1 }}>
          {subClusters.map((subCluster, index) => (
            <Paper
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                onSubClusterToggle(subCluster);
              }}
              elevation={0}
              sx={{
                p: 1,
                mb: 0.5,
                cursor: 'pointer',
                backgroundColor: selectedSubClusters.includes(subCluster) ? '#fff5f0' : '#fff',
                border: selectedSubClusters.includes(subCluster) ? '1px solid #ff5200' : '1px solid #e0e0e0',
                borderRadius: 1,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                '&:hover': {
                  backgroundColor: '#fff5f0',
                  borderColor: '#ff5200'
                }
              }}
            >
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  border: selectedSubClusters.includes(subCluster) ? '2px solid #ff5200' : '2px solid #ccc',
                  backgroundColor: selectedSubClusters.includes(subCluster) ? '#ff5200' : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.2s ease'
                }}
              >
                {selectedSubClusters.includes(subCluster) && (
                  <CheckIcon sx={{ fontSize: 12, color: '#fff' }} />
                )}
              </Box>
              <Typography
                variant="caption"
                sx={{
                  color: selectedSubClusters.includes(subCluster) ? '#ff5200' : '#666',
                  fontWeight: selectedSubClusters.includes(subCluster) ? 600 : 400,
                  fontSize: '0.75rem',
                  lineHeight: 1.4
                }}
              >
                {subCluster}
              </Typography>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  );
};

// Step 1: Geography, Industry & Tier Selection
export const GeographyChannelMapping: React.FC<{
  onComplete: (data: SelectionData, topPubs?: any[]) => void;
  onCorpCrunchSelect: () => void;
  initialCriteria?: SelectionData;
  prId?: string | null;
}> = ({ onComplete, onCorpCrunchSelect, initialCriteria, prId }) => {
  const [selectedGeography, setSelectedGeography] = useState<string[]>(initialCriteria?.geography || []);
  const [selectedIndustry, setSelectedIndustry] = useState<string[]>(initialCriteria?.industry || []);
  // Filter out 'corp-crunch' from tier array - it's tracked separately in isCorpCrunchSelected
  const [selectedTier, setSelectedTier] = useState<string[]>(
    (initialCriteria?.tier || []).filter(t => t !== 'corp-crunch')
  );
  const [showAllCountries, setShowAllCountries] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [expandedIndustry, setExpandedIndustry] = useState<string>('');
  const [selectedSubClusters, setSelectedSubClusters] = useState<string[]>(initialCriteria?.subClusters || []);
  const [packageType, setPackageType] = useState<'standalone' | 'package'>(initialCriteria?.packageType || 'standalone');
  const [quantity, setQuantity] = useState(initialCriteria?.quantity || 1);
  const [selectedPriorityPublications, setSelectedPriorityPublications] = useState<string[]>(initialCriteria?.priorityPublications || []);
  const [topPublications, setTopPublications] = useState<any[]>([]);
  const [loadingPublications, setLoadingPublications] = useState(false);
  const [isCorpCrunchSelected, setIsCorpCrunchSelected] = useState(initialCriteria?.tier?.includes('corp-crunch') || false);
  const [activeWishlistTier, setActiveWishlistTier] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [wishlistLoaded, setWishlistLoaded] = useState(false);
  const [wishlistSaved, setWishlistSaved] = useState(false);

  const mainGeographies = [
    { id: 'global', name: 'Global', icon: <PublicIcon />, desc: 'Worldwide distribution' },
    { id: 'north-america', name: 'North America', icon: <LanguageIcon />, desc: 'USA, Canada, Mexico' },
    { id: 'south-america', name: 'South America', icon: <LanguageIcon />, desc: 'Brazil, Argentina, Colombia, Chile' },
    { id: 'europe', name: 'Europe', icon: <LanguageIcon />, desc: 'UK, Germany, France & more' },
    { id: 'asia', name: 'Asia Pacific', icon: <LanguageIcon />, desc: 'India, China, Japan, Singapore' },
    { id: 'middle-east', name: 'Middle East', icon: <LanguageIcon />, desc: 'UAE, Saudi Arabia, Qatar' },
    { id: 'africa', name: 'Africa', icon: <LanguageIcon />, desc: 'South Africa, Nigeria, Kenya' }
  ];


  const industryOptions = Object.keys(industryClusters).map(name => ({
    id: name.toLowerCase().replace(/ /g, '-'),
    name: name,
    icon: <BusinessIcon />,
    desc: `${name} focused publications`
  }));

  const tiers = [
    { id: 'corp-crunch', name: 'Corp Crunch', icon: <StarIcon />, desc: '', isExclusive: true },
    { id: '1', name: 'Tier 1', icon: <StarIcon />, desc: 'Major national outlets' },
    { id: '2', name: 'Tier 2', icon: <StarIcon />, desc: 'Industry-specific publications' },
    { id: '3', name: 'Tier 3', icon: <StarIcon />, desc: 'Regional newspapers & media' },
    { id: '4', name: 'Tier 4', icon: <StarIcon />, desc: 'Niche blogs & portals' },
    { id: '5', name: 'Tier 5', icon: <StarIcon />, desc: 'Trade journals & magazines' }
  ];

  const allChannelsForSearch = publications.map((publication) => ({
    id: `${publication.tier}-${publication.name.toLowerCase().replace(/\s+/g, '-')}`,
    _id: `${publication.tier}-${publication.name.toLowerCase().replace(/\s+/g, '-')}`,
    name: publication.name,
    tier: publication.tier,
    domainAuthority: calculateDA(publication.tier),
    monthlyReach: calculateMonthlyReach(publication.tier),
    country: publication.country,
    region: publication.region,
    primaryIndustry: publication.primaryIndustry,
    secondaryIndustry: publication.secondaryIndustry,
    outletType: publication.outletType,
    language: publication.language,
    coverageNotes: publication.coverageNotes
  }));

  // Add this helper function
  const matchesIndustry = (publication: any, selectedIndustries: string[]): boolean => {
    if (selectedIndustries.length === 0) return true;

    return selectedIndustries.some(selected => {
      const selectedLower = selected.toLowerCase().replace(/-/g, ' ');
      const primaryMatch = publication.primaryIndustry?.toLowerCase().includes(selectedLower);
      const secondaryMatch = publication.secondaryIndustry?.toLowerCase().includes(selectedLower);

      // Also check reverse - if industry name contains selected cluster
      const reverseMatch = selectedLower.includes(publication.primaryIndustry?.toLowerCase()) ||
        selectedLower.includes(publication.secondaryIndustry?.toLowerCase());

      return primaryMatch || secondaryMatch || reverseMatch;
    });
  };

  // Add this helper function
  const matchesGeography = (publication: any, selectedGeographies: string[]): boolean => {
    if (selectedGeographies.length === 0) return true;

    // Check if 'global' is selected
    if (selectedGeographies.includes('global')) return true;

    // Check if publication region is 'Global'
    if (publication.region === 'Global') return true;

    return selectedGeographies.some(selectedGeo => {
      const normalizedGeo = selectedGeo.toLowerCase().replace(/-/g, ' ');

      // Check if publication country matches
      const countryMatch = publication.country?.toLowerCase().includes(normalizedGeo) ||
        normalizedGeo.includes(publication.country?.toLowerCase());

      // Check if publication region matches
      const regionMatch = publication.region?.toLowerCase().includes(normalizedGeo) ||
        normalizedGeo.includes(publication.region?.toLowerCase());

      // Check REGION_MAPPING
      const countriesInRegion = publications.filter((p) => p.region === selectedGeo).map((p) => p.country);
      const mappingMatch = countriesInRegion.some(country =>
        country.toLowerCase() === publication.country?.toLowerCase()
      );

      return countryMatch || regionMatch || mappingMatch;
    });
  };

  // Load wishlist from backend on mount
  useEffect(() => {
    const loadWishlist = async () => {
      try {
        const response = await getUserWishlist();

        // Check if wishlist exists and has items
        if (response.hasWishlist && response.wishlist && response.wishlist.length > 0) {
          // Load and lock the saved wishlist
          setSelectedPriorityPublications(response.wishlist);
          setWishlistSaved(true);
        } else {
          setWishlistSaved(false);
        }
        setWishlistLoaded(true);
      } catch (error) {
        console.error('❌ Failed to load wishlist:', error);
        console.error('❌ Error details:', error);
        setWishlistLoaded(true);
        setWishlistSaved(false);
      }
    };

    loadWishlist();
  }, []);

  // Auto-save wishlist when selections change (whenever 6 publications are selected)
  useEffect(() => {
    const saveWishlist = async () => {
      // Auto-save if:
      // 1. User has selected 6 publications
      // 2. Wishlist has been loaded (not on initial mount)
      if (selectedPriorityPublications.length === 6 && wishlistLoaded) {
        try {
          const result = await saveUserWishlist(selectedPriorityPublications);
          setWishlistSaved(true);
        } catch (error) {
          console.error('❌ Failed to save wishlist:', error);
        }
      }
    };

    const timeoutId = setTimeout(saveWishlist, 1000);
    return () => clearTimeout(timeoutId);
  }, [selectedPriorityPublications, wishlistLoaded]);

  // Fetch top publications based on PR content
  useEffect(() => {
    const fetchTopPublications = async () => {
      // If wishlist is already saved and loaded, skip heavy fetching/ranking
      // and just use a small static fallback set. This keeps the UI instant
      // on subsequent visits while still showing the saved wishlist.
      if (wishlistSaved && wishlistLoaded) {
        const fallbackPubs = publications.slice(0, 6).map((publication) => ({
          id: `${publication.tier}-${publication.name.toLowerCase().replace(/\s+/g, '-')}`,
          name: publication.name,
          tier: publication.tier,
          domainAuthority: calculateDA(publication.tier),
          country: publication.country,
          regions: publication.region
        }));
        setTopPublications(fallbackPubs);
        setLoadingPublications(false);
        return;
      }

      setLoadingPublications(true);

      const fallbackPubs = publications.slice(0, 4).map((publication) => ({ id: `${publication.tier}-${publication.name.toLowerCase().replace(/\s+/g, '-')}`, name: publication.name, tier: publication.tier, domainAuthority: calculateDA(publication.tier), country: publication.country, regions: publication.region }));



      if (!prId) {
        setTopPublications(fallbackPubs);
        setLoadingPublications(false);
        return;
      }

      try {
        // First get all channels
        let channels;
        try {
          channels = await getChannels();
        } catch (channelError) {
          console.warn('⚠️ Failed to fetch channels from API, using static data:', channelError);
          // Use static publications as fallback
          channels = publications.map((pub, idx) => ({
            _id: `pub-${idx}`,
            id: `pub-${idx}`,
            name: pub.name,
            tier: pub.tier,
            country: pub.country,
            email: generateEmail(pub.website),
            domainAuthority: calculateDA(pub.tier),
            monthlyReach: calculateMonthlyReach(pub.tier)
          }));
        }

        if (!channels || channels.length === 0) {
          console.warn('No channels available, using fallbacks');
          setTopPublications(fallbackPubs);
          setLoadingPublications(false);
          return;
        }

        // Convert to publication format with proper IDs
        const publicationsList = channels.slice(0, 30).map((channel: any, idx) => ({
          id: channel._id || channel.id || `pub-${idx}`,
          _id: channel._id || channel.id || `pub-${idx}`,
          name: channel.name || `Publication ${idx + 1}`,
          tier: channel.tier || 1,
          domainAuthority: channel.domainAuthority || calculateDA(channel.tier || 1),
          monthlyReach: channel.monthlyReach || calculateMonthlyReach(channel.tier || 1),
          engagementRate: 5,
          topicRelevance: 85,
          country: channel.country || 'Global',
          email: channel.email || generateEmail(channel.name || ''),
          geographicRegions: [],

        }));

        // Use AI ranking if available
        try {
          const rankedPubs = await aiRankPublications(publicationsList, prId);
          if (rankedPubs && rankedPubs.length > 0) {
            setTopPublications(rankedPubs.slice(0, 20)); // Top 20 for wishlist
          } else {
            // If AI ranking returns empty, use the original publications
            setTopPublications(publicationsList.slice(0, 20));
          }
        } catch (aiError) {
          console.warn('⚠️ AI ranking failed, using default order:', aiError);
          // Fallback to original publications if AI ranking fails
          setTopPublications(publicationsList.slice(0, 20));
        }
      } catch (error) {
        console.error('Error fetching top publications:', error);
        setTopPublications(fallbackPubs);
      } finally {
        setLoadingPublications(false);
      }
    };

    fetchTopPublications();
  }, [prId, wishlistLoaded, wishlistSaved]);

  const handlePriorityPublicationToggle = (pubId: string) => {
    // Always allow changes - wishlist is always editable
    setSelectedPriorityPublications(prev => {
      if (prev.includes(pubId)) {
        return prev.filter(id => id !== pubId);
      } else if (prev.length < 6) {
        return [...prev, pubId];
      }
      return prev; // Max 6 selected
    });
  };

  const handleToggleExpand = (industryId: string, industryName: string) => {
    const isExpanding = expandedIndustry !== industryId;
    setExpandedIndustry(isExpanding ? industryId : '');

    // Auto-select the main cluster when expanding
    if (isExpanding && !selectedIndustry.includes(industryId)) {
      setSelectedIndustry(prev => [...prev, industryId]);
    }
  };

  const handleSubClusterToggle = (subCluster: string, industryId: string) => {
    // Auto-select the main industry if not already selected
    if (!selectedIndustry.includes(industryId)) {
      setSelectedIndustry(prev => [...prev, industryId]);
    }

    setSelectedSubClusters(prev =>
      prev.includes(subCluster)
        ? prev.filter(sc => sc !== subCluster)
        : [...prev, subCluster]
    );
  };

  const handleIndustryClick = (industryId: string) => {
    setSelectedIndustry(prev =>
      prev.includes(industryId)
        ? prev.filter(id => id !== industryId)
        : [...prev, industryId]
    );
  };

  const handleGeographyToggle = (geoId: string) => {
    setSelectedGeography(prev =>
      prev.includes(geoId)
        ? prev.filter(id => id !== geoId)
        : [...prev, geoId]
    );
  };

  const handleTierToggle = (tierId: string) => {
    if (tierId === 'corp-crunch') {
      // Toggle Corp Crunch independently
      setIsCorpCrunchSelected(prev => !prev);
      return;
    }

    // Toggle regular tiers independently
    setSelectedTier(prev =>
      prev.includes(tierId)
        ? prev.filter(id => id !== tierId)
        : [...prev, tierId]
    );
  };

  const handleProceedToCheckout = () => {
    // Validate selections
    if (selectedGeography.length === 0 || selectedIndustry.length === 0) {
      alert('Please select at least one geography and industry');
      return;
    }

    // Check if at least one tier (including Corp Crunch) is selected
    if (!isCorpCrunchSelected && selectedTier.length === 0) {
      alert('Please select at least one tier');
      return;
    }

    // Combine regular tiers and Corp Crunch if selected
    const allTiers = [...selectedTier];
    if (isCorpCrunchSelected) {
      allTiers.push('corp-crunch');
    }

    // Pass ALL channels that contain the selected publications, not just topPublications
    // This ensures all 6 selected publications can be found in Step 4
    const selectedPublicationsData = allChannelsForSearch.filter((pub: any) =>
      selectedPriorityPublications.includes(pub.id)
    );

    onComplete({
      geography: selectedGeography,
      industry: selectedIndustry,
      tier: allTiers,
      subClusters: selectedSubClusters,
      packageType,
      quantity,
      priorityPublications: selectedPriorityPublications
    }, selectedPublicationsData.length === selectedPriorityPublications.length ? selectedPublicationsData : allChannelsForSearch);

    // If Corp Crunch is among selections, trigger the handler
    if (isCorpCrunchSelected) {
      onCorpCrunchSelect();
    }
  };

  const isValid = (isCorpCrunchSelected && selectedGeography.length > 0 && selectedIndustry.length > 0) ||
    (selectedGeography.length > 0 && selectedIndustry.length > 0 && selectedTier.length > 0);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: { xs: 3, md: 5 } }}>

        {/* Loading state while wishlist is being fetched */}
        {!wishlistLoaded && (
          <Box sx={{ mb: 6, textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ color: '#ff5200' }} />
            <Typography variant="body2" sx={{ mt: 2, color: '#666' }}>
              Loading your wishlist...
            </Typography>
          </Box>
        )}

        {/* Top Publication Wishlist (Priority) */}
        {/* Always show UI - always editable */}
        {wishlistLoaded && (
          <Box sx={{ mb: 6 }}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, md: 3 },
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.03)'
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    p: 1,
                    borderRadius: 2,
                    backgroundColor: '#fff5f0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <StarIcon sx={{ color: '#ff5200' }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#09273b', letterSpacing: '-0.01em' }}>
                      Top Publication Wishlist
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                      Prioritize up to 6 outlets for your campaign
                    </Typography>
                  </Box>
                </Box>

                {/* Selected Slots Visualization */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {[...Array(6)].map((_, i) => {
                    const selectedId = selectedPriorityPublications[i];
                    const pub = topPublications.find(p => Number(p.tier) === Number(activeWishlistTier) && p.id === selectedId);
                    return (
                      <Box
                        key={i}
                        sx={{
                          width: { xs: 35, md: 45 },
                          height: { xs: 35, md: 45 },
                          borderRadius: 2,
                          border: selectedId ? '2px solid #ff5200' : '2px dashed #e0e0e0',
                          backgroundColor: selectedId ? '#fff5f0' : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: selectedId ? 'pointer' : 'default',
                          transition: 'all 0.2s ease',
                          position: 'relative',
                          '&:hover': selectedId ? { transform: 'scale(1.05)', borderColor: '#e64a00' } : {}
                        }}
                        onClick={() => selectedId && handlePriorityPublicationToggle(selectedId)}
                        title={pub?.name || 'Empty slot'}
                      >
                        {selectedId ? (
                          <Typography sx={{ fontWeight: 800, color: '#ff5200', fontSize: '0.8rem' }}>
                            {i + 1}
                          </Typography>
                        ) : (
                          <AddIcon sx={{ color: '#ccc', fontSize: 18 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* Search Bar */}
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  placeholder="Search publications by name across all tiers..."
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setShowSearchResults(e.target.value.length > 0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: '#666' }}>
                        <SearchIcon />
                      </Box>
                    ),
                    endAdornment: searchQuery && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchQuery('');
                          setShowSearchResults(false);
                        }}
                        sx={{ color: '#666' }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      '& fieldset': {
                        borderColor: '#e0e0e0',
                      },
                      '&:hover fieldset': {
                        borderColor: '#ff5200',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff5200',
                        borderWidth: '2px',
                      }
                    }
                  }}
                />
              </Box>

              {/* Description and Tier Tabs Row */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: 3 }}>
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 600, fontSize: '0.95rem' }}>
                  Select the publication channels based on tiers
                </Typography>

                {/* Tier Tabs */}
                <Tabs
                  value={activeWishlistTier}
                  onChange={(_, val) => {
                    setActiveWishlistTier(val);
                    setShowSearchResults(false);
                    setSearchQuery('');
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTabs-indicator': { backgroundColor: '#ff5200', height: 3, borderRadius: '3px 3px 0 0' },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      color: '#666',
                      minWidth: 80,
                      minHeight: 42,
                      '&.Mui-selected': { color: '#ff5200' }
                    }
                  }}
                >
                  <Tab label="Tier 1" value={1} icon={<LayersIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                  <Tab label="Tier 2" value={2} icon={<LayersIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                  <Tab label="Tier 3" value={3} icon={<LayersIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                  <Tab label="Tier 4" value={4} icon={<LayersIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                  <Tab label="Tier 5" value={5} icon={<LayersIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
                </Tabs>
              </Box>

              {/* Search Results - Grouped by Tier */}
              {showSearchResults && searchQuery && (
                <Box
                  sx={{
                    mb: 3,
                    maxHeight: '600px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '8px'
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: '#f0f0f0',
                      borderRadius: '10px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: '#ff5200',
                      borderRadius: '10px',
                      '&:hover': {
                        backgroundColor: '#e64a00'
                      }
                    }
                  }}
                >
                  {[1, 2, 3, 4, 5].map((tierNum) => {
                    // ✅ Apply ALL filters
                    const filteredPubs = allChannelsForSearch.filter((p: any) => {
                      // Match tier
                      if (p.tier !== tierNum) return false;

                      // Match search query
                      if (!p.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;

                      // Match geography (if selected)
                      if (selectedGeography.length > 0 && !matchesGeography(p, selectedGeography)) return false;

                      // Match industry (if selected)
                      if (selectedIndustry.length > 0 && !matchesIndustry(p, selectedIndustry)) return false;

                      return true;
                    });

                    if (filteredPubs.length === 0) return null;



                    return (
                      <Box key={tierNum} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1, py: 1 }}>
                          <LayersIcon sx={{ color: '#ff5200', fontSize: 20 }} />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#09273b' }}>
                            Tier {tierNum} Results ({filteredPubs.length})
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                            gap: 2,
                            pb: 2
                          }}
                        >
                          {filteredPubs.map((pub) => {
                            const isSelected = selectedPriorityPublications.includes(pub.id);
                            const isDisabled = !isSelected && selectedPriorityPublications.length >= 6;

                            return (
                              <Paper
                                key={pub.id}
                                onClick={() => !isDisabled && handlePriorityPublicationToggle(pub.id)}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                  backgroundColor: isSelected ? '#ff5200' : '#fafafa',
                                  border: isSelected ? '2px solid #09273b' : '1px solid #e0e0e0',
                                  borderRadius: 2,
                                  transition: 'all 0.3s ease',
                                  position: 'relative',
                                  opacity: isDisabled ? 0.6 : 1,
                                  height: '100%',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  '&:hover': isDisabled ? {} : {
                                    borderColor: '#ff5200',
                                    backgroundColor: isSelected ? '#e64a00' : '#fff',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(255, 82, 0, 0.2)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1,
                                    backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#fff',
                                    border: isSelected ? 'none' : '1px solid #eee',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    {isSelected ? (
                                      <CheckCircleIcon sx={{ color: '#fff', fontSize: 20 }} />
                                    ) : (
                                      <PublicIcon sx={{ color: '#ccc', fontSize: 18 }} />
                                    )}
                                  </Box>
                                  <Chip
                                    label={`DA ${pub.domainAuthority || 90}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                                      color: isSelected ? '#fff' : '#09273b',
                                      fontWeight: 700,
                                      fontSize: '0.65rem',
                                      height: 20,
                                      border: isSelected ? '1px solid rgba(255,255,255,0.3)' : 'none'
                                    }}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: isSelected ? '#fff' : '#09273b',
                                    mb: 0.5,
                                    fontSize: '0.9rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                >
                                  {pub.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.6)' : '#666', fontSize: '0.7rem' }}>
                                    {pub.country || 'Global'}
                                  </Typography>
                                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: isSelected ? 'rgba(255,255,255,0.3)' : '#ddd' }} />
                                  <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.6)' : '#666', fontSize: '0.7rem' }}>
                                    Tier {pub.tier}
                                  </Typography>
                                </Box>
                                {isSelected && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    backgroundColor: '#09273b',
                                    color: '#fff',
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '0.75rem',
                                    boxShadow: '0 2px 8px rgba(9, 39, 59, 0.4)',
                                    border: '2px solid #fff'
                                  }}>
                                    {selectedPriorityPublications.indexOf(pub.id) + 1}
                                  </Box>
                                )}
                              </Paper>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  })}
                  {allChannelsForSearch.filter(p =>
                    p.name.toLowerCase().includes(searchQuery.toLowerCase())
                  ).length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body2" sx={{ color: '#999' }}>
                          No publications found matching "{searchQuery}"
                        </Typography>
                      </Box>
                    )}
                </Box>
              )}

              {loadingPublications ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 2 }}>
                  <CircularProgress size={40} thickness={4} sx={{ color: '#ff5200' }} />
                  <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                    Curating publications...
                  </Typography>
                </Box>
              ) : !showSearchResults ? (
                <>
                  {/* Selected Wishlist Publications - Horizontal Scrollable Row */}
                  {selectedPriorityPublications.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                          Your Selected Wishlist ({selectedPriorityPublications.length}/6)
                        </Typography>
                        {wishlistSaved && (
                          <Chip
                            label="Saved"
                            size="small"
                            icon={<CheckCircleIcon />}
                            sx={{
                              backgroundColor: '#4caf50',
                              color: '#fff',
                              fontWeight: 600,
                              fontSize: '0.65rem',
                              height: '20px',
                              '& .MuiChip-icon': { color: '#fff', fontSize: '14px' }
                            }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 2,
                          flexWrap: 'wrap',
                          pb: 1
                        }}
                      >
                        {allChannelsForSearch
                          .filter((p: any) => selectedPriorityPublications.includes(p.id))
                          .map((pub: any) => {
                            const isSelected = selectedPriorityPublications.includes(pub.id);
                            const isDisabled = !isSelected && selectedPriorityPublications.length >= 6;

                            return (
                              <Paper
                                key={pub.id}
                                onClick={() => handlePriorityPublicationToggle(pub.id)}
                                elevation={0}
                                sx={{
                                  p: 1.5,
                                  width: {
                                    xs: 'calc((100% - 16px) / 2)',  // 2 cards on mobile
                                    sm: 'calc((100% - 32px) / 3)',  // 3 cards on small screens
                                    md: 'calc((100% - 80px) / 6)'   // All 6 cards visible on desktop
                                  },
                                  minWidth: {
                                    xs: '140px',  // Minimum for mobile
                                    sm: '160px',  // Minimum for small screens
                                    md: '150px'   // Smaller minimum for desktop to fit 6 cards
                                  },
                                  maxWidth: {
                                    xs: 'calc((100% - 16px) / 2)',
                                    sm: 'calc((100% - 32px) / 3)',
                                    md: 'calc((100% - 80px) / 6)'
                                  },
                                  cursor: 'pointer',
                                  backgroundColor: '#fff5f0',
                                  border: '2px solid #ff5200',
                                  borderRadius: 2,
                                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                  position: 'relative',
                                  flexShrink: 0,
                                  '&:hover': {
                                    backgroundColor: '#fff',
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 25px rgba(255, 82, 0, 0.25)'
                                  }
                                }}
                              >
                                {/* Compact card content */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{
                                    width: 28,
                                    height: 28,
                                    borderRadius: 1,
                                    backgroundColor: isSelected ? '#fff5f0' : '#fff',
                                    border: isSelected ? '2px solid #ff5200' : '1px solid #eee',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}>
                                    {isSelected ? (
                                      <CheckCircleIcon sx={{ color: '#ff5200', fontSize: 18 }} />
                                    ) : (
                                      <PublicIcon sx={{ color: '#ccc', fontSize: 16 }} />
                                    )}
                                  </Box>
                                  <Chip
                                    label={`DA ${pub.domainAuthority || 90}`}
                                    size="small"
                                    sx={{
                                      backgroundColor: isSelected ? '#ff5200' : '#f0f0f0',
                                      color: isSelected ? '#fff' : '#09273b',
                                      fontWeight: 700,
                                      fontSize: '0.6rem',
                                      height: '20px'
                                    }}
                                  />
                                </Box>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: '#09273b',
                                    mb: 0.5,
                                    fontSize: '0.8rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                  }}
                                  title={pub.name}
                                >
                                  {pub.name}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.65rem' }}>
                                    {pub.country || 'Global'}
                                  </Typography>
                                  <Box sx={{ width: 3, height: 3, borderRadius: '50%', backgroundColor: '#ddd' }} />
                                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, fontSize: '0.65rem' }}>
                                    Tier {pub.tier}
                                  </Typography>
                                </Box>

                                {isSelected && (
                                  <Box sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    backgroundColor: '#09273b',
                                    color: '#fff',
                                    width: 22,
                                    height: 22,
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 800,
                                    fontSize: '0.7rem',
                                    boxShadow: '0 3px 8px rgba(9, 39, 59, 0.3)',
                                    border: '2px solid #fff'
                                  }}>
                                    {selectedPriorityPublications.indexOf(pub.id) + 1}
                                  </Box>
                                )}
                              </Paper>
                            );
                          })}
                      </Box>
                    </Box>
                  )}

                  {/* Available Publications Grid - Browse and Select */}
                  {/* Hide this section when 6 publications are selected */}
                  {selectedPriorityPublications.length < 6 && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: '#666', fontWeight: 700, mb: 1.5, textTransform: 'uppercase', fontSize: '0.75rem' }}>
                        Browse & Select Publications - Tier {activeWishlistTier} ({selectedPriorityPublications.length}/6 selected)
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: {
                            xs: 'repeat(2, 1fr)',
                            sm: 'repeat(3, 1fr)',
                            md: 'repeat(4, 1fr)',
                            lg: 'repeat(5, 1fr)',
                            xl: 'repeat(6, 1fr)'
                          },
                          gap: 2,
                          maxHeight: '300px', // Reduced to show only 3 rows
                          overflowY: 'auto',
                          pr: 1,
                          '&::-webkit-scrollbar': {
                            width: '8px'
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f0f0f0',
                            borderRadius: '10px'
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#ff5200',
                            borderRadius: '10px',
                            '&:hover': {
                              backgroundColor: '#e64a00'
                            }
                          }
                        }}
                      >
                        {allChannelsForSearch
                          .filter(p => Number(p.tier) === Number(activeWishlistTier))
                          .length > 0 ? (
                          allChannelsForSearch
                            .filter((p: any) => Number(p.tier) === Number(activeWishlistTier))
                            .map((pub: any) => {
                              const isSelected = selectedPriorityPublications.includes(pub.id);
                              const isDisabled = !isSelected && selectedPriorityPublications.length >= 6;

                              return (
                                <Paper
                                  key={pub.id}
                                  onClick={() => !isDisabled && handlePriorityPublicationToggle(pub.id)}
                                  elevation={0}
                                  sx={{
                                    p: 2,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                    backgroundColor: isSelected ? '#ff5200' : '#fafafa',
                                    border: isSelected ? '2px solid #09273b' : '1px solid #e0e0e0',
                                    borderRadius: 2,
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    opacity: isDisabled ? 0.5 : 1,
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '&:hover': isDisabled ? {} : {
                                      borderColor: '#ff5200',
                                      backgroundColor: isSelected ? '#e64a00' : '#fff',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(255, 82, 0, 0.2)'
                                    }
                                  }}
                                >
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Box sx={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: 1,
                                      backgroundColor: isSelected ? '#fff' : '#fff',
                                      border: isSelected ? '2px solid #fff' : '1px solid #eee',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      boxShadow: isSelected ? '0 2px 6px rgba(0,0,0,0.15)' : 'none'
                                    }}>
                                      {isSelected ? (
                                        <CheckCircleIcon sx={{ color: '#ff5200', fontSize: 18 }} />
                                      ) : (
                                        <PublicIcon sx={{ color: '#ccc', fontSize: 16 }} />
                                      )}
                                    </Box>
                                    <Chip
                                      label={`DA ${pub.domainAuthority || 90}`}
                                      size="small"
                                      sx={{
                                        backgroundColor: isSelected ? 'rgba(255,255,255,0.2)' : '#f0f0f0',
                                        color: isSelected ? '#fff' : '#09273b',
                                        fontWeight: 700,
                                        fontSize: '0.6rem',
                                        height: 18,
                                        border: isSelected ? '1px solid rgba(255,255,255,0.3)' : 'none'
                                      }}
                                    />
                                  </Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontWeight: 700,
                                      color: isSelected ? '#fff' : '#09273b',
                                      mb: 0.5,
                                      fontSize: '0.85rem',
                                      lineHeight: 1.3,
                                      display: '-webkit-box',
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      minHeight: '2.6em'
                                    }}
                                    title={pub.name}
                                  >
                                    {pub.name}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 'auto' }}>
                                    <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255,255,255,0.7)' : '#666', fontSize: '0.65rem' }}>
                                      {pub.country || 'Global'}
                                    </Typography>
                                  </Box>
                                  {isSelected && (
                                    <Box sx={{
                                      position: 'absolute',
                                      top: -8,
                                      right: -8,
                                      backgroundColor: '#09273b',
                                      color: '#fff',
                                      width: 22,
                                      height: 22,
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 800,
                                      fontSize: '0.7rem',
                                      boxShadow: '0 2px 8px rgba(9, 39, 59, 0.4)',
                                      border: '2px solid #fff'
                                    }}>
                                      {selectedPriorityPublications.indexOf(pub.id) + 1}
                                    </Box>
                                  )}
                                </Paper>
                              );
                            })
                        ) : (
                          <Box sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" sx={{ color: '#999' }}>
                              No Tier {activeWishlistTier} publications available
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              ) : null}

              {selectedPriorityPublications.length > 0 && (
                <Box sx={{
                  mt: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2
                }}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 700, textTransform: 'uppercase' }}>
                    Selection:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', flex: 1 }}>
                    {selectedPriorityPublications.map((pubId, idx) => {
                      const pub = allChannelsForSearch.find((p: any) => p.id === pubId);
                      return pub ? (
                        <Chip
                          key={pubId}
                          label={`${idx + 1}. ${pub.name}`}
                          size="small"
                          onDelete={() => handlePriorityPublicationToggle(pubId)}
                          sx={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #eee',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            '& .MuiChip-deleteIcon': { color: '#ff5200', fontSize: '14px' }
                          }}
                        />
                      ) : null;
                    })}
                  </Box>

                  {/* Clear All button always visible */}
                  <Button
                    size="small"
                    onClick={() => setSelectedPriorityPublications([])}
                    sx={{ color: '#ff5200', fontWeight: 700, textTransform: 'none' }}
                  >
                    Clear All
                  </Button>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Show saved wishlist if exists */}

        {/* Divider with Title */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 2 }}>
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
            <Box sx={{ flex: 1, height: '1px', backgroundColor: '#e0e0e0' }} />
          </Box>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b', mb: 1, fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Select Geography & Industry cluster
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              Choose your preferences to find the perfect publication channels
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={3} sx={{ alignItems: 'stretch', mb: 4 }}>
          {/* Left Column - Geography */}
          <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
            <Paper sx={{
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: 3,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '900px',
              maxHeight: '900px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <LocationIcon sx={{ color: '#ff5200' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b' }}>Target Geography</Typography>
              </Box>

              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                {/* Main Regions */}
                <Typography variant="caption" sx={{ color: '#666', fontWeight: 600, mb: 1, display: 'block' }}>REGIONS</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                  {mainGeographies.map((geo) => (
                    <SelectionCard key={geo.id} icon={geo.icon} title={geo.name} description={geo.desc}
                      selected={selectedGeography.includes(geo.id)} onClick={() => handleGeographyToggle(geo.id)} multiSelect compact />
                  ))}
                </Box>

                {/* Countries Toggle */}
                <Button
                  onClick={() => setShowAllCountries(!showAllCountries)}
                  sx={{ color: '#ff5200', textTransform: 'none', fontWeight: 600, mb: 2 }}
                >
                  {showAllCountries ? '− Hide Countries' : '+ Show All Countries'}
                </Button>

                {/* All Countries */}
                {showAllCountries && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', fontWeight: 600, mb: 1, display: 'block' }}
                    >
                      COUNTRIES
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Search country..."
                      value={countrySearch}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      fullWidth
                      sx={{ mb: 1.5 }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {allCountries
                        .filter((country) =>
                          countrySearch
                            ? country.toLowerCase().includes(countrySearch.toLowerCase())
                            : true,
                        )
                        .map((country) => {
                          const countryId = country.toLowerCase().replace(/ /g, '-');
                          const isSelected = selectedGeography.includes(countryId);
                          return (
                            <Paper
                              key={country}
                              onClick={() => handleGeographyToggle(countryId)}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                cursor: 'pointer',
                                borderRadius: 1,
                                backgroundColor: isSelected ? '#ff5200 !important' : '#fff !important',
                                color: isSelected ? '#fff !important' : '#09273b !important',
                                border: isSelected ? '2px solid #ff5200' : '1px solid #e0e0e0',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  backgroundColor: isSelected ? '#ff5200 !important' : '#f5f5f5 !important',
                                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                                }
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'inherit !important' }}>{country}</Typography>
                            </Paper>
                          );
                        })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Middle Column - Industry */}
          <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
            <Paper sx={{
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: 3,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '900px',
              maxHeight: '900px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <TopicIcon sx={{ color: '#ff5200' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b' }}>Industry Cluster</Typography>
              </Box>
              <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                {industryOptions.map((industry) => (
                  <IndustrySelectionCard
                    key={industry.id}
                    icon={industry.icon}
                    title={industry.name}
                    description={industry.desc}
                    selected={selectedIndustry.includes(industry.id)}
                    onClick={() => handleIndustryClick(industry.id)}
                    subClusters={industryClusters[industry.name as keyof typeof industryClusters] || []}
                    expanded={expandedIndustry === industry.id}
                    onToggleExpand={() => handleToggleExpand(industry.id, industry.name)}
                    selectedSubClusters={selectedSubClusters}
                    onSubClusterToggle={(subCluster) => handleSubClusterToggle(subCluster, industry.id)}
                    industryId={industry.id}
                  />
                ))}
              </Box>
            </Paper>
          </Grid>

          {/* Third Column - Tier Selection */}
          <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
            <Paper sx={{
              p: 3,
              backgroundColor: '#f8f9fa',
              borderRadius: 3,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              minHeight: '900px',
              maxHeight: '900px'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <LayersIcon sx={{ color: '#ff5200' }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b' }}>Publication Tier</Typography>
              </Box>
              <Box sx={{
                flex: 1,
                overflowY: 'auto',
                pr: 1,
                '&::-webkit-scrollbar': {
                  display: 'none'
                },
                scrollbarWidth: 'none',
                msOverflowStyle: 'none'
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {tiers.map((tier) => {
                    const isSelected = tier.id === 'corp-crunch' ? isCorpCrunchSelected : selectedTier.includes(tier.id);
                    return (
                      <Paper
                        key={tier.id}
                        onClick={() => handleTierToggle(tier.id)}
                        elevation={0}
                        sx={{
                          p: 2.5,
                          cursor: 'pointer',
                          backgroundColor: isSelected ? '#fff' : '#fafafa',
                          border: isSelected ? '3px solid #ff5200' : '1px solid #e0e0e0',
                          borderRadius: 2,
                          transition: 'all 0.2s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          position: 'relative',
                          '&:hover': {
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                            transform: 'translateX(4px)'
                          }
                        }}
                      >
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2,
                            backgroundColor: isSelected ? '#ff5200' : '#f0f0f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease',
                            flexShrink: 0
                          }}
                        >
                          {React.cloneElement(tier.icon as React.ReactElement, {
                            sx: { fontSize: 22, color: isSelected ? '#fff' : '#09273b' }
                          })}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 600,
                              color: '#09273b',
                              wordBreak: 'break-word',
                              lineHeight: 1.3
                            }}
                          >
                            {tier.name}
                          </Typography>
                          {tier.desc && (
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem', mt: 0.5 }}>
                              {tier.desc}
                            </Typography>
                          )}
                        </Box>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '4px',
                            border: isSelected ? '2px solid #ff5200' : '2px solid #ccc',
                            backgroundColor: isSelected ? '#ff5200' : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {isSelected && <CheckIcon sx={{ fontSize: 16, color: '#fff' }} />}
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Fourth Column - Package, Quantity & Pricing */}
          <Grid item xs={12} md={3} sx={{ display: 'flex' }}>
            {(selectedTier.length > 0 || isCorpCrunchSelected) ? (
              <Paper sx={{
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 3,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: '900px',
                maxHeight: '900px'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <TrendingUpIcon sx={{ color: '#ff5200' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b' }}>Pricing</Typography>
                </Box>
                <Box sx={{
                  flex: 1,
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    display: 'none'
                  },
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none'
                }}>
                  {/* Package Type Selection - Standalone Only */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#09273b', mb: 1.5 }}>
                      Package Type
                    </Typography>
                    <Paper
                      elevation={2}
                      sx={{
                        p: 2,
                        backgroundColor: '#ff5200 !important',
                        border: '2px solid #ff5200',
                        borderRadius: 1.5,
                        position: 'relative'
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          backgroundColor: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14, color: '#ff5200' }} />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#fff !important',
                          mb: 0.5
                        }}
                      >
                        Standalone
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#fff !important',
                          fontSize: '0.75rem'
                        }}
                      >
                        30 days validity
                      </Typography>
                    </Paper>
                  </Box>

                  {/* Quantity Selector */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#09273b', mb: 1.5 }}>
                      Quantity
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <IconButton
                          size="small"
                          onClick={() => setQuantity(Math.max(1, quantity - 1))}
                          sx={{
                            border: '1px solid #e0e0e0',
                            '&:hover': { borderColor: '#ff5200', backgroundColor: '#fff5f0' }
                          }}
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                        <TextField
                          value={quantity}
                          onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                          type="number"
                          size="small"
                          sx={{
                            width: 80,
                            '& input': { textAlign: 'center', fontWeight: 700, fontSize: '1.2rem', color: '#09273b', py: 1 },
                            '& fieldset': { borderColor: '#e0e0e0' }
                          }}
                          inputProps={{ min: 1, style: { textAlign: 'center' } }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => setQuantity(quantity + 1)}
                          sx={{
                            border: '1px solid #e0e0e0',
                            '&:hover': { borderColor: '#ff5200', backgroundColor: '#fff5f0' }
                          }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Price Summary */}
                  <Paper elevation={0} sx={{ p: 2.5, backgroundColor: '#fff5f0', border: '2px solid #ff5200', borderRadius: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ color: '#09273b', display: 'block', mb: 2, fontWeight: 700 }}>
                      Price Summary
                    </Typography>
                    {(() => {
                      // Combine regular tiers and Corp Crunch if selected
                      // Filter out corp-crunch from selectedTier first to avoid duplicates
                      const regularTiers = selectedTier.filter(t => t !== 'corp-crunch');
                      const selectedTiersForPricing = [...regularTiers];
                      if (isCorpCrunchSelected) {
                        selectedTiersForPricing.push('corp-crunch');
                      }
                      const priceData = calculateItemPrice('standalone', selectedTiersForPricing, 1);

                      return (
                        <>
                          {priceData.tierBreakdown.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              {priceData.tierBreakdown.map((tier, idx) => (
                                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                  <Typography variant="caption" sx={{ color: '#666' }}>
                                    {tier.tierName || `Tier ${tier.tier}`}:
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#09273b' }}>
                                    ${tier.price.toLocaleString()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              Unit Price:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#09273b' }}>
                              ${priceData.pricePerUnit.toLocaleString()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              Quantity:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#09273b' }}>
                              ×{quantity}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 1.5 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#09273b' }}>
                              Total:
                            </Typography>
                            <Typography variant="h5" sx={{ fontWeight: 700, color: '#ff5200' }}>
                              ${calculateItemPrice('standalone', selectedTiersForPricing, quantity).totalPrice.toLocaleString()}
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', textAlign: 'center' }}>
                            Valid for 30 days
                          </Typography>
                        </>
                      );
                    })()}
                  </Paper>
                  {/* Proceed Button - in 4th card, outside pricing summary box */}
                  <Box sx={{ mt: 3 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={!isValid}
                      onClick={handleProceedToCheckout}
                      endIcon={<ArrowForwardIcon />}
                      sx={{
                        py: 1.5,
                        backgroundColor: '#ff5200',
                        color: '#fff',
                        fontWeight: 700,
                        textTransform: 'none',
                        borderRadius: 1.5,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: '#e04800',
                          boxShadow: 'none',
                        },
                        '&:disabled': {
                          backgroundColor: '#ccc',
                        },
                      }}
                    >
                      Proceed to Checkout
                    </Button>
                  </Box>
                </Box>
              </Paper>
            ) : (
              <Paper sx={{
                p: 3,
                backgroundColor: '#f8f9fa',
                borderRadius: 3,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px'
              }}>
                <LayersIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                <Typography variant="body2" sx={{ color: '#999', textAlign: 'center' }}>
                  Select tiers to see pricing
                </Typography>
              </Paper>
            )}
          </Grid>
        </Grid>

        {/* Order Summary (no extra checkout button here) */}
        {isValid && (
          <Box sx={{ mt: 5 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={8}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', border: '2px solid #e0e0e0', borderRadius: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b', mb: 3 }}>
                    Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                          Selected Geography
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                          {selectedGeography.length} region{selectedGeography.length !== 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {selectedGeography.slice(0, 2).join(', ')}
                          {selectedGeography.length > 2 && ` +${selectedGeography.length - 2} more`}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                          Selected Industries
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                          {selectedIndustry.length} industry cluster{selectedIndustry.length !== 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          {selectedIndustry.slice(0, 2).join(', ')}
                          {selectedIndustry.length > 2 && ` +${selectedIndustry.length - 2} more`}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                          Publication Tiers
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                          {selectedTier.length} tier{selectedTier.length !== 1 ? 's' : ''} selected
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#999' }}>
                          Tier {selectedTier.join(', ')}
                        </Typography>
                      </Box>
                    </Grid>
                    {selectedSubClusters.length > 0 && (
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Box>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                            Sub-Clusters Selected
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#ff5200' }}>
                            {selectedSubClusters.length} sub-cluster{selectedSubClusters.length !== 1 ? 's' : ''}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#999' }}>
                            {selectedSubClusters.slice(0, 3).join(', ')}
                            {selectedSubClusters.length > 3 && ` +${selectedSubClusters.length - 3} more`}
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>
    </Container>
  );
};

// CircularPublicationItem is now in RealTimePublicationScanning component

// Stripe instance for checkout - only if key exists and in HTTPS
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Helper to safely load Stripe and swallow script load errors so they don't crash the app
const createStripePromise = () => {
  if (!stripeKey || !shouldLoadStripe()) return null;
  try {
    return loadStripe(stripeKey).catch((err) => {
      console.error('❌ Failed to load Stripe.js:', err);
      return null;
    });
  } catch (err) {
    console.error('❌ Exception while initializing Stripe.js:', err);
    return null;
  }
};

const stripePromise = createStripePromise();

// Diagnostic logging
if (process.env.NODE_ENV === 'development') {
  if (!stripeKey) {
    console.error('❌ [DistributionInterface] REACT_APP_STRIPE_PUBLISHABLE_KEY is missing!');
  } else if (!stripePromise) {
    console.warn('⚠️  [DistributionInterface] Stripe promise is null');
  }
}

// RealTimePublicationScanning is now in a separate file - imported above

// Step 2.5: Package Selection
export const PackageSelection: React.FC<{
  onSelect: (packageData: PackageData, includeCorpCrunch: boolean) => void;
  onBack: () => void;
  selectionData?: SelectionData;
}> = ({ onSelect, onBack, selectionData }) => {
  const [selectedPackage, setSelectedPackage] = useState<PackageData | null>(null);
  const [customTiers, setCustomTiers] = useState<string[]>(selectionData?.tier || []);
  const [customPRs, setCustomPRs] = useState<number>(20);
  const initializedRef = useRef(false); // Track if we've initialized from step 1
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(true);
  // Track PR count per tier for custom package
  const [tierPRCounts, setTierPRCounts] = useState<{ [tier: string]: number }>({
    '1': 0,
    '2': 0,
    '3': 0,
    '4': 0,
    '5': 0
  });
  // Validation message for custom package (e.g. minimum PRs)
  const [customPackageError, setCustomPackageError] = useState<string | null>(null);

  // Get selected tiers from step 1
  const selectedTiersFromStep1 = selectionData?.tier || [];
  const regularTiersFromStep1 = selectedTiersFromStep1.filter(t => t !== 'corp-crunch');
  const [includeCorpCrunch, setIncludeCorpCrunch] = useState<boolean>(selectedTiersFromStep1.includes('corp-crunch'));

  // Initialize customTiers with selected tiers from step 1 if not already set
  useEffect(() => {
    if (selectedTiersFromStep1.length > 0 && customTiers.length === 0) {
      setCustomTiers(selectedTiersFromStep1);
    }
  }, [selectedTiersFromStep1]);

  // Keep Corp Crunch add-on in sync with Step 1 selection
  useEffect(() => {
    setIncludeCorpCrunch(selectedTiersFromStep1.includes('corp-crunch'));
  }, [selectedTiersFromStep1.join(',')]);

  // Check if a tier matches the selected tiers
  const isTierSelected = (tier: string) => {
    if (tier === '4-5') {
      return selectedTiersFromStep1.includes('4') || selectedTiersFromStep1.includes('5');
    }
    return selectedTiersFromStep1.includes(tier);
  };

  // Standalone pricing per PR
  const standalonePrices = {
    '1': 3999,
    '2': 2999,
    '3': 1999,
    '4': 999,
    '5': 999
  };

  const packages = [
    {
      id: 'tier1',
      label: 'Tier 1',
      tier: '1',
      price: 69999,
      prs: 20,
      color: '#09273b',
      standalonePrice: standalonePrices['1'] * 20,
      name: 'Enterprise',
      features: ['Global, National, and Regional coverage', 'Advanced analytics', 'Dedicated Acc. Manager', 'Priority support']
    },
    {
      id: 'tier2',
      label: 'Tier 2',
      tier: '2',
      price: 52999,
      prs: 20,
      color: '#09273b',
      standalonePrice: standalonePrices['2'] * 20,
      name: 'Premium',
      features: ['National & Regional coverage', 'Advanced analytics', 'Dedicated Acc. Manager', '24/7 Customer service']
    },
    {
      id: 'tier3',
      label: 'Tier 3',
      tier: '3',
      price: 34999,
      prs: 20,
      color: '#09273b',
      standalonePrice: standalonePrices['3'] * 20,
      name: 'Pro',
      features: ['National & Regional coverage', 'Advanced analytics', '24/7 Customer service', 'Standard analytics']
    },
    {
      id: 'tier4-5',
      label: 'Tier 4 & 5',
      tier: '4-5',
      price: 17999,
      prs: 20,
      color: '#09273b',
      standalonePrice: standalonePrices['4'] * 20,
      name: 'Basic',
      features: ['National & Regional coverage', 'Standard analytics', '24/7 Customer service', 'Basic support']
    },
  ];

  // Calculate savings for each package
  const calculateSavings = (pkg: typeof packages[0]) => {
    const savings = pkg.standalonePrice - pkg.price;
    const savingsPercent = Math.round((savings / pkg.standalonePrice) * 100);
    const pricePerPR = Math.round(pkg.price / pkg.prs);
    return { savings, savingsPercent, pricePerPR };
  };

  const handlePackageSelect = (pkg: typeof packages[0]) => {
    setSelectedPackage({
      type: 'package',
      tier: pkg.tier,
      price: pkg.price,
      credits: pkg.prs
    });
  };

  // Initialize selectedPackage based on step 1 selection (only once when selectionData is available)
  useEffect(() => {
    if (!initializedRef.current && selectionData && !selectedPackage) {
      initializedRef.current = true; // Mark as initialized

      if (selectionData.packageType === 'standalone') {
        // If standalone was selected in step 1, auto-select standalone
        if (regularTiersFromStep1.length === 0 && selectionData.tier.includes('corp-crunch')) {
          setSelectedPackage({
            type: 'standalone',
            tier: 'corp-crunch',
            price: 1499,
            credits: 1
          });
        } else {
          setSelectedPackage({
            type: 'standalone',
            price: 0, // Will be calculated based on selection
            credits: 0
          });
        }
      } else if (selectionData.packageType === 'package' && selectedTiersFromStep1.length > 0) {
        // If package was selected in step 1, find matching package by tier
        const matchingPackage = packages.find(pkg => {
          if (pkg.tier === '4-5') {
            return selectedTiersFromStep1.includes('4') || selectedTiersFromStep1.includes('5');
          }
          return selectedTiersFromStep1.includes(pkg.tier);
        });

        if (matchingPackage) {
          setSelectedPackage({
            type: 'package',
            tier: matchingPackage.tier,
            price: matchingPackage.price,
            credits: matchingPackage.prs
          });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData]); // Run when selectionData is first available

  // Check scroll position on mount and resize
  useEffect(() => {
    const checkScroll = () => {
      if (scrollContainerRef.current) {
        const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
        setShowLeftScroll(scrollLeft > 10);
        setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
      }
    };

    // Initial check with multiple timeouts to ensure it runs after render
    const timeout1 = setTimeout(checkScroll, 100);
    const timeout2 = setTimeout(checkScroll, 300);
    const timeout3 = setTimeout(checkScroll, 500);

    window.addEventListener('resize', checkScroll);
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
    }

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      window.removeEventListener('resize', checkScroll);
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, []);

  // Calculate custom package price based on tier PR counts
  const calculateCustomPackagePrice = (tierCounts: { [tier: string]: number }): { total: number; breakdown: Array<{ tier: string, prCount: number, price: number }> } => {
    // Get package pricing per PR for each tier
    const tierPricesPerPR: { [key: string]: number } = {
      '1': 69999 / 20, // 3499.95 per PR
      '2': 52999 / 20, // 2649.95 per PR
      '3': 34999 / 20, // 1749.95 per PR
      '4': 17999 / 20, // 899.95 per PR
      '5': 17999 / 20  // 899.95 per PR
    };

    let total = 0;
    const breakdown: Array<{ tier: string, prCount: number, price: number }> = [];

    // Calculate price for each tier: (price per PR × PR count)
    Object.keys(tierCounts).forEach(tier => {
      const prCount = tierCounts[tier] || 0;
      if (prCount > 0) {
        const pricePerPR = tierPricesPerPR[tier] || 0;
        const tierTotal = pricePerPR * prCount;
        total += tierTotal;
        breakdown.push({
          tier,
          prCount,
          price: Math.round(tierTotal)
        });
      }
    });

    return {
      total: Math.round(total),
      breakdown
    };
  };

  // Legacy function for backward compatibility (used in some places)
  const calculateCustomPackagePriceLegacy = (tiers: string[], prs: number): number => {
    if (tiers.length === 0 || prs === 0) return 0;
    const tierCounts: { [key: string]: number } = {};
    tiers.forEach(tier => {
      tierCounts[tier] = prs;
    });
    return calculateCustomPackagePrice(tierCounts).total;
  };

  const handleCustomPackage = () => {
    // Calculate total PRs from tier counts
    const totalPRs = Object.values(tierPRCounts).reduce((sum, count) => sum + count, 0);

    // Enforce minimum 5 PRs for custom package
    if (totalPRs < 5) {
      if (totalPRs > 0) {
        setCustomPackageError('Custom package requires a minimum of 5 PRs.');
      } else {
        setCustomPackageError('Please select PR counts for tiers (minimum 5 PRs total).');
      }
      return;
    }

    setCustomPackageError(null);

    if (totalPRs > 0) {
      // Get tiers that have PR counts > 0
      const activeTiers = Object.keys(tierPRCounts).filter(tier => (tierPRCounts[tier] || 0) > 0);

      // Calculate custom package price using tier counts
      const { total } = calculateCustomPackagePrice(tierPRCounts);

      setSelectedPackage({
        type: 'custom',
        customTiers: activeTiers,
        customPRs: totalPRs,
        tierPRCounts,
        price: total,
        credits: totalPRs
      });
    }
  };

  const handleTierPRCountChange = (tier: string, count: number) => {
    const newCount = Math.max(0, count);
    setTierPRCounts(prev => ({
      ...prev,
      [tier]: newCount
    }));

    // Auto-update selected package when counts change
    const updatedCounts = { ...tierPRCounts, [tier]: newCount };
    const totalPRs = Object.values(updatedCounts).reduce((sum, c) => sum + c, 0);

    // Custom package minimum PR validation when user is typing
    if (totalPRs === 0) {
      setCustomPackageError(null);
    } else if (totalPRs < 5) {
      setCustomPackageError('Custom package requires a minimum of 5 PRs.');
    } else {
      setCustomPackageError(null);
    }

    if (totalPRs > 0) {
      const activeTiers = Object.keys(updatedCounts).filter(t => (updatedCounts[t] || 0) > 0);
      const { total } = calculateCustomPackagePrice(updatedCounts);

      setSelectedPackage({
        type: 'custom',
        customTiers: activeTiers,
        customPRs: totalPRs,
        tierPRCounts: updatedCounts, // Include tier PR counts breakdown
        price: total,
        credits: totalPRs
      });
    }
  };

  const handleCorpCrunch = () => {
    setIncludeCorpCrunch(prev => {
      const isNowIncluded = !prev;

      if (isNowIncluded && !selectedPackage) {
        setSelectedPackage({
          type: 'standalone',
          tier: 'corp-crunch',
          price: 1499,
          credits: 1
        });
      } else if (!isNowIncluded && selectedPackage?.type === 'standalone' && selectedPackage?.tier === 'corp-crunch') {
        setSelectedPackage(null);
      }

      return isNowIncluded;
    });
  };

  const handleStandalone = () => {
    // Toggle Standalone selection - can deselect if already selected
    if (selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch') {
      if (includeCorpCrunch) {
        setSelectedPackage({
          type: 'standalone',
          tier: 'corp-crunch',
          price: 1499,
          credits: 1
        });
      } else {
        setSelectedPackage(null); // Deselect
      }
    } else {
      setSelectedPackage({
        type: 'standalone',
        price: 0, // Will be calculated based on selection
        credits: 0
      });
    }
  };


  const handleContinue = () => {
    if (!selectedPackage) {
      alert('Please select a package');
      return;
    }

    // Enforce minimum 5 PRs for custom package before continuing
    if (selectedPackage.type === 'custom') {
      const totalPRs =
        selectedPackage.customPRs ??
        Object.values(selectedPackage.tierPRCounts || {}).reduce((sum, c) => sum + c, 0);
      if (!totalPRs || totalPRs < 5) {
        setCustomPackageError('Custom package requires a minimum of 5 PRs before continuing.');
        alert('Custom package requires a minimum of 5 PRs.');
        return;
      }
    }
    onSelect(selectedPackage, includeCorpCrunch);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, overflowX: 'hidden', px: { xs: 2, md: 5 } }}>
      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          sx={{
            borderColor: '#09273b',
            color: '#09273b',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            borderWidth: '2px',
            '&:hover': {
              borderColor: '#ff5200',
              backgroundColor: '#fff5f0',
              color: '#ff5200',
              borderWidth: '2px'
            }
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={!selectedPackage}
          endIcon={<ArrowForwardIcon />}
          sx={{
            backgroundColor: '#ff5200',
            fontWeight: 600,
            px: 4,
            py: 1.5,
            '&:hover': { backgroundColor: '#e04800' },
            '&:disabled': { backgroundColor: '#ccc' }
          }}
        >
          Continue
        </Button>
      </Box>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b', mb: 1 }}>
          Select Packages
        </Typography>
        <Typography variant="h6" sx={{ color: '#ff5200', fontWeight: 600, mb: 0.5 }}>
          Save up to 12.5% with yearly packages!
        </Typography>
        <Typography variant="body1" sx={{ color: '#666' }}>
          Choose a package that fits your needs and save money on bulk purchases
        </Typography>
      </Box>

      {/* Disclaimer */}
      <Alert
        severity="info"
        icon={<InfoIcon />}
        sx={{
          mb: 4,
          backgroundColor: '#E3F2FD',
          color: '#1976D2',
          borderRadius: 2,
          '& .MuiAlert-icon': { color: '#1976D2' }
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          *All PRs should be exhausted within 365 days of its purchase.
        </Typography>
      </Alert>

      {/* Package Cards Container */}
      <Box sx={{ position: 'relative', mb: 4, width: '100%', maxWidth: '100%', overflow: 'hidden', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Left Scroll Button */}
        {showLeftScroll && (
          <IconButton
            onClick={() => {
              if (scrollContainerRef.current) {
                const cardWidth = scrollContainerRef.current.clientWidth / 4;
                scrollContainerRef.current.scrollBy({ left: -cardWidth * 2, behavior: 'smooth' });
              }
            }}
            sx={{
              flexShrink: 0,
              backgroundColor: '#ff5200',
              border: 'none',
              transition: 'all 0.3s ease',
              width: 44,
              height: 44,
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(255, 82, 0, 0.3)',
              '&:hover': {
                backgroundColor: '#e64a00',
                transform: 'scale(1.1)',
                boxShadow: '0 4px 12px rgba(255, 82, 0, 0.4)'
              },
              '& .MuiSvgIcon-root': { color: '#fff', fontSize: 28 }
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        )}

        <Box
          ref={scrollContainerRef}
          onScroll={() => {
            if (scrollContainerRef.current) {
              const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
              setShowLeftScroll(scrollLeft > 10);
              setShowRightScroll(scrollLeft < scrollWidth - clientWidth - 10);
            }
          }}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            overflowY: 'hidden',
            mb: 4,
            justifyContent: 'flex-start',
            flexWrap: 'nowrap',
            width: '100%',
            maxWidth: '100%',
            scrollBehavior: 'smooth',
            scrollSnapType: 'x mandatory',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            msOverflowStyle: 'none',
            px: 0.5,
            boxSizing: 'border-box',
            '& > *': { flexShrink: 0 }
          }}
        >
          {/* STATIC ORDER 1: STANDALONE CARD */}
          {/* Show standalone card when regular tiers (non corp-crunch) are selected */}
          {selectedTiersFromStep1.filter(t => t !== 'corp-crunch').length > 0 && (
            <Box sx={{ flex: '0 0 calc((100% - 48px) / 4)', minWidth: 'calc((100% - 48px) / 4)', maxWidth: 'calc((100% - 48px) / 4)', width: 'calc((100% - 48px) / 4)', scrollSnapAlign: 'start', boxSizing: 'border-box' }}>
              <Card
                elevation={selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? 4 : 1}
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: '380px',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  border: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '2px solid #ff5200' : '1px solid #e0e0e0',
                  backgroundColor: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '#ff5200 !important' : '#fff !important',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  '&:hover': { transform: 'translateY(-3px)', boxShadow: 3, borderColor: '#ff5200' }
                }}
                onClick={handleStandalone}
              >
                <CardContent sx={{ p: { xs: 2, md: 2 }, textAlign: 'center', '&:last-child': { pb: { xs: 2, md: 2 } } }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '#fff !important' : '#09273b !important', fontSize: '1rem' }}>
                    Standalone
                  </Typography>
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '#fff !important' : '#09273b !important', lineHeight: 1.1, fontSize: '1.75rem' }}>
                      {selectionData && regularTiersFromStep1.length > 0
                        ? `$${calculateItemPrice('standalone', regularTiersFromStep1, selectionData.quantity).pricePerUnit.toLocaleString()}`
                        : '$0'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '#fff !important' : '#666 !important', fontSize: '0.75rem' }}>
                      Pay per publication
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'left', mb: 1.5 }}>
                    {regularTiersFromStep1.length > 0 ? (
                      regularTiersFromStep1.map((tier) => (
                        <Box key={tier} sx={{ display: 'flex', alignItems: 'center', mb: 0.75 }}>
                          <CheckCircleIcon sx={{ fontSize: 14, color: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '#fff' : '#4CAF50', mr: 0.75 }} />
                          <Typography variant="body2" sx={{ color: selectedPackage?.type === 'standalone' && selectedPackage?.tier !== 'corp-crunch' ? '#fff !important' : '#666 !important', fontSize: '0.8rem', fontWeight: 600 }}>
                            Tier {tier}: ${standalonePrices[tier as keyof typeof standalonePrices].toLocaleString()}/PR
                          </Typography>
                        </Box>
                      ))
                    ) : (
                      <Typography variant="caption" sx={{ color: '#999 !important', fontSize: '0.7rem', fontStyle: 'italic' }}>
                        Select tiers in step 1
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}

          {/* STATIC ORDER 2: CORP CRUNCH CARD - Always show */}
          <Box sx={{ flex: '0 0 calc((100% - 48px) / 4)', minWidth: 'calc((100% - 48px) / 4)', maxWidth: 'calc((100% - 48px) / 4)', width: 'calc((100% - 48px) / 4)', scrollSnapAlign: 'start', boxSizing: 'border-box' }}>
            <Card
              elevation={includeCorpCrunch ? 4 : 1}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: '380px',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                border: includeCorpCrunch ? '2px solid #ff5200' : '1px solid #e0e0e0',
                backgroundColor: includeCorpCrunch ? '#ff5200 !important' : '#fff !important',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 3, borderColor: '#ff5200' }
              }}
              onClick={handleCorpCrunch}
            >
              <CardContent sx={{ p: { xs: 2, md: 2 }, textAlign: 'center', '&:last-child': { pb: { xs: 2, md: 2 } } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: includeCorpCrunch ? '#fff !important' : '#09273b !important', fontSize: '1rem' }}>
                  Corp Crunch
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: includeCorpCrunch ? '#fff !important' : '#09273b !important', lineHeight: 1.1, fontSize: '1.75rem' }}>
                    $1,499
                  </Typography>
                </Box>
                <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: includeCorpCrunch ? 'rgba(255, 255, 255, 0.2)' : '#f8f9fa', borderRadius: 1.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: includeCorpCrunch ? '#fff !important' : '#09273b !important', fontSize: '0.85rem' }}>
                    Premium Tier 1 Publication
                  </Typography>
                  <Typography variant="caption" sx={{ color: includeCorpCrunch ? '#fff !important' : '#666 !important', fontSize: '0.7rem' }}>
                    Skip the queue and publish directly
                  </Typography>
                </Box>
                <Box sx={{ mt: 1.5, textAlign: 'left', maxHeight: '100px', overflowY: 'auto' }}>
                  {['50M+ Monthly Reach', '95 Domain Authority', '24 Hours Publish Time', 'Guaranteed Placement'].map((feature, idx) => (
                    <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75 }}>
                      <CheckCircleIcon sx={{ fontSize: 14, color: includeCorpCrunch ? '#fff' : '#4CAF50', mr: 0.75, mt: 0.2, flexShrink: 0 }} />
                      <Typography variant="caption" sx={{ color: includeCorpCrunch ? '#fff !important' : '#666 !important', fontSize: '0.7rem', lineHeight: 1.4 }}>
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* STATIC ORDER 3: YEARLY PACKAGES */}
          {packages.slice().reverse().map((pkg) => {
            const { pricePerPR } = calculateSavings(pkg);
            const isSelected = selectedPackage?.tier === pkg.tier && selectedPackage?.type === 'package';
            const isTierMatched = isTierSelected(pkg.tier);

            return (
              <Box key={pkg.id} sx={{ display: 'flex', flex: '0 0 calc((100% - 48px) / 4)', minWidth: 'calc((100% - 48px) / 4)', maxWidth: 'calc((100% - 48px) / 4)', width: 'calc((100% - 48px) / 4)', scrollSnapAlign: 'start', boxSizing: 'border-box' }}>
                <Card
                  elevation={isSelected ? 4 : 1}
                  sx={{
                    width: '100%',
                    height: '100%',
                    minHeight: '380px',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: 'pointer',
                    border: isSelected ? '2px solid #ff5200' : isTierMatched ? '1.5px solid #ff5200' : '1px solid #e0e0e0',
                    backgroundColor: isSelected ? '#ff5200 !important' : '#fff !important',
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: 3, borderColor: '#ff5200' }
                  }}
                  onClick={() => handlePackageSelect(pkg)}
                >
                  <CardContent sx={{ p: { xs: 2, md: 2 }, textAlign: 'center', position: 'relative', '&:last-child': { pb: { xs: 2, md: 2 } } }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: isSelected ? '#fff !important' : '#09273b !important', fontSize: '1rem' }}>
                      {pkg.name}
                    </Typography>
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: isSelected ? '#fff !important' : '#09273b !important', lineHeight: 1.1, fontSize: '1.75rem' }}>
                        ${pkg.price.toLocaleString()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: isSelected ? '#fff !important' : '#666 !important', fontSize: '0.75rem' }}>
                        ${pricePerPR.toLocaleString()} per PR
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 1.5, p: 1.5, backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.2)' : '#f8f9fa', borderRadius: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: isSelected ? '#fff !important' : '#09273b !important', fontSize: '0.85rem' }}>
                        {pkg.prs} PRs - {pkg.label}
                      </Typography>
                      <Typography variant="caption" sx={{ color: isSelected ? '#fff !important' : '#666 !important', fontSize: '0.7rem' }}>
                        Valid for 365 days
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1.5, textAlign: 'left', maxHeight: '100px', overflowY: 'auto' }}>
                      {pkg.features.slice(0, 3).map((feature, idx) => (
                        <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.75 }}>
                          <CheckCircleIcon sx={{ fontSize: 14, color: isSelected ? '#fff' : '#4CAF50', mr: 0.75, mt: 0.2, flexShrink: 0 }} />
                          <Typography variant="caption" sx={{ color: isSelected ? '#fff !important' : '#666 !important', fontSize: '0.7rem', lineHeight: 1.4 }}>
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: isSelected ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="caption" sx={{ color: isSelected ? '#fff !important' : '#666 !important', fontSize: '0.7rem' }}>
                        Standalone:
                      </Typography>
                      <Typography variant="caption" sx={{ color: isSelected ? 'rgba(255, 255, 255, 0.8) !important' : '#999 !important', textDecoration: 'line-through', fontSize: '0.7rem' }}>
                        ${pkg.standalonePrice.toLocaleString()}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            );
          })}

          {/* STATIC ORDER 4: CUSTOM PACKAGE CARD */}
          <Box sx={{ display: 'flex', flex: '0 0 calc((100% - 48px) / 4)', minWidth: 'calc((100% - 48px) / 4)', maxWidth: 'calc((100% - 48px) / 4)', width: 'calc((100% - 48px) / 4)', scrollSnapAlign: 'start', boxSizing: 'border-box' }}>
            <Card
              elevation={selectedPackage?.type === 'custom' ? 4 : 1}
              sx={{
                width: '100%',
                cursor: 'pointer',
                border: selectedPackage?.type === 'custom' ? '2px solid #ff5200' : selectedTiersFromStep1.length > 1 ? '1.5px solid #ff5200' : '1px solid #e0e0e0',
                backgroundColor: selectedPackage?.type === 'custom' ? '#ff5200 !important' : '#fff !important',
                transition: 'all 0.3s ease',
                position: 'relative',
                '&:hover': { transform: 'translateY(-3px)', boxShadow: 3, borderColor: '#ff5200' }
              }}
              onClick={handleCustomPackage}
            >
              <CardContent sx={{ p: { xs: 2, md: 2 }, textAlign: 'center', '&:last-child': { pb: { xs: 2, md: 2 } } }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: selectedPackage?.type === 'custom' ? '#fff !important' : '#09273b !important', fontSize: '1rem' }}>
                  Custom Package
                </Typography>
                <Box sx={{ mb: 1.5, textAlign: 'left' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {[
                      { tier: '1', label: 'Tier 1' },
                      { tier: '2', label: 'Tier 2' },
                      { tier: '3', label: 'Tier 3' },
                      { tier: '4', label: 'Tier 4&5' }
                    ].map(({ tier, label }) => {
                      const count = tier === '4' ? (tierPRCounts['4'] + tierPRCounts['5']) : tierPRCounts[tier];
                      return (
                        <Box key={tier} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 0.75, borderRadius: 1, backgroundColor: count > 0 ? 'rgba(255, 82, 0, 0.05)' : 'transparent' }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: selectedPackage?.type === 'custom' ? '#fff' : '#09273b', fontSize: '0.75rem' }}>{label}</Typography>
                          <TextField
                            type="number"
                            size="small"
                            value={count}
                            onChange={(e) => handleTierPRCountChange(tier, parseInt(e.target.value) || 0)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ width: '55px', '& input': { textAlign: 'center', padding: '4px', fontSize: '0.75rem' } }}
                          />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
                <Box sx={{ mt: 1.5, p: 1, backgroundColor: '#ffffff', borderRadius: 1.5, border: '2px solid #ffffff' }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: '#09273b', fontSize: '0.9rem' }}>
                    Total: ${calculateCustomPackagePrice(tierPRCounts).total.toLocaleString()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Right Scroll Button */}
        {showRightScroll && (
          <IconButton
            onClick={() => {
              if (scrollContainerRef.current) {
                const cardWidth = scrollContainerRef.current.clientWidth / 4;
                scrollContainerRef.current.scrollBy({ left: cardWidth * 2, behavior: 'smooth' });
              }
            }}
            sx={{
              flexShrink: 0,
              backgroundColor: '#ff5200',
              border: 'none',
              transition: 'all 0.3s ease',
              width: 44,
              height: 44,
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(255, 82, 0, 0.3)',
              '&:hover': { backgroundColor: '#e64a00', transform: 'scale(1.1)' },
              '& .MuiSvgIcon-root': { color: '#fff', fontSize: 28 }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        )}
      </Box>

      {/* Selected Package Summary */}
      {selectedPackage && (() => {
        const selectedPkg = selectedPackage.type === 'package'
          ? packages.find(p => p.tier === selectedPackage.tier)
          : null;
        const savingsInfo = selectedPkg ? calculateSavings(selectedPkg) : null;

        return (
          <Paper
            elevation={2}
            sx={{
              p: 4, mb: 4,
              backgroundColor: '#fff5f0',
              border: '2px solid #ff5200',
              borderRadius: 2
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>
                Selected Package Summary
              </Typography>
              {selectedPackage.type === 'package' && savingsInfo && (
                <Chip
                  icon={<LocalOfferIcon />}
                  label={`Save $${savingsInfo.savings.toLocaleString()} (${savingsInfo.savingsPercent}%)`}
                  sx={{
                    backgroundColor: '#4CAF50',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    py: 2.5,
                    px: 1
                  }}
                />
              )}
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
                    Package Type
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#09273b', fontWeight: 600 }}>
                    {selectedPackage.type === 'package' ? 'Yearly Package' : selectedPackage.type === 'custom' ? 'Custom Package' : 'Standalone (Pay per PR)'}
                  </Typography>
                </Box>

                {selectedPackage.tier && selectedPackage.type !== 'standalone' && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
                      Tier
                    </Typography>
                    <Typography variant="body1" sx={{ color: '#09273b', fontWeight: 600 }}>
                      {selectedPkg?.name || `Tier ${selectedPackage.tier}`}
                    </Typography>
                  </Box>
                )}

                {/* Show selected tiers for standalone */}
                {selectedPackage.type === 'standalone' && (selectedPackage.tier !== 'corp-crunch' ? selectedTiersFromStep1.length > 0 : includeCorpCrunch) && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
                      Selected Tiers
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedPackage.tier !== 'corp-crunch' && selectedTiersFromStep1.filter(t => t !== 'corp-crunch').map((tier) => (
                        <Chip
                          key={tier}
                          label={`Tier ${tier}`}
                          size="small"
                          sx={{ backgroundColor: '#ff5200', color: '#fff', fontWeight: 600 }}
                        />
                      ))}
                      {includeCorpCrunch && (
                        <Chip
                          label="Corp Crunch"
                          size="small"
                          sx={{ backgroundColor: '#ff5200', color: '#fff', fontWeight: 600 }}
                        />
                      )}
                    </Box>
                  </Box>
                )}

                {selectedPackage.customTiers && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
                      Selected Tiers
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectedPackage.customTiers.map((tier) => (
                        <Chip
                          key={tier}
                          label={tier === 'corp-crunch' ? 'Corp Crunch' : `Tier ${tier}`}
                          size="small"
                          sx={{ backgroundColor: '#ff5200', color: '#fff', fontWeight: 600 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
                    Credits (PRs Included)
                  </Typography>
                  <Typography variant="body1" sx={{ color: '#09273b', fontWeight: 600 }}>
                    {selectedPackage.credits} PRs {selectedPackage.type !== 'standalone' && '(Valid for 365 days)'}
                  </Typography>
                </Box>

                {selectedPackage.type === 'package' && savingsInfo && (
                  <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(76, 175, 80, 0.1)', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      Price per PR: <strong>${savingsInfo.pricePerPR.toLocaleString()}</strong>
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      Standalone Price: <span style={{ textDecoration: 'line-through' }}>${selectedPkg?.standalonePrice.toLocaleString()}</span>
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 700, mt: 1 }}>
                      You Save: ${savingsInfo.savings.toLocaleString()} ({savingsInfo.savingsPercent}% discount)
                    </Typography>
                  </Box>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box sx={{ textAlign: { xs: 'left', md: 'right' }, mt: { xs: 2, md: 0 } }}>
                  <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                    Total Price
                  </Typography>
                  <Box>
                    {(() => {
                      let total = selectedPackage.price;
                      let standaloneBase = 0;
                      if (selectedPackage.type === 'standalone' && selectedPackage.tier === 'corp-crunch') {
                        total = 1499;
                        standaloneBase = 0;
                      } else if (selectedPackage.type === 'standalone' && selectionData && regularTiersFromStep1.length > 0) {
                        const { totalPrice } = calculateItemPrice('standalone', regularTiersFromStep1, selectionData.quantity);
                        standaloneBase = totalPrice;
                        total = standaloneBase + (includeCorpCrunch ? 1499 : 0);
                      } else {
                        total = selectedPackage.price + (includeCorpCrunch ? 1499 : 0);
                      }

                      return (
                        <>
                          <Typography variant="h3" sx={{ fontWeight: 800, color: '#ff5200', mb: 1 }}>
                            ${total.toLocaleString()}
                          </Typography>
                          {selectedPackage.type === 'package' && savingsInfo && (
                            <Typography variant="body2" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                              ${savingsInfo.pricePerPR.toLocaleString()} per PR
                            </Typography>
                          )}
                          <Box sx={{ mt: 2, textAlign: { xs: 'left', md: 'right' } }}>
                            <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5, fontWeight: 600 }}>
                              Price Breakdown:
                            </Typography>
                            {selectedPackage.type === 'standalone' && selectedPackage.tier !== 'corp-crunch' && selectionData && regularTiersFromStep1.length > 0 && (
                              <Typography variant="body2" sx={{ color: '#09273b', fontWeight: 600, mb: 0.25 }}>
                                Standalone tiers: ${standaloneBase.toLocaleString()}
                              </Typography>
                            )}
                            {selectedPackage.type !== 'standalone' && (
                              <Typography variant="body2" sx={{ color: '#09273b', fontWeight: 600, mb: 0.25 }}>
                                Package cost: ${(selectedPackage.price).toLocaleString()}
                              </Typography>
                            )}
                            {includeCorpCrunch && (
                              <Typography variant="body2" sx={{ color: '#09273b', fontWeight: 600, mb: 0.25 }}>
                                Corp Crunch add-on: $1,499
                              </Typography>
                            )}
                          </Box>
                        </>
                      );
                    })()}

                    {/* Show selected tiers breakdown for standalone or when Corp Crunch is selected */}
                    {selectedPackage.type === 'standalone' && (selectedPackage.tier !== 'corp-crunch' ? selectedTiersFromStep1.length > 0 : includeCorpCrunch) && (
                      <Box sx={{ mt: 2, textAlign: { xs: 'left', md: 'right' } }}>
                        <Typography variant="subtitle2" sx={{ color: '#666', mb: 1, fontWeight: 600 }}>
                          Selected Tiers:
                        </Typography>
                        {includeCorpCrunch && (
                          <Typography variant="body2" sx={{ color: '#09273b', fontWeight: 600, mb: 0.5 }}>
                            Corp Crunch - Premium Tier 1
                          </Typography>
                        )}
                        {selectedPackage.tier !== 'corp-crunch' && selectedTiersFromStep1.filter(t => t !== 'corp-crunch').map((tier) => (
                          <Typography key={tier} variant="body2" sx={{ color: '#09273b', fontWeight: 600, mb: 0.5 }}>
                            Tier {tier}: ${standalonePrices[tier as keyof typeof standalonePrices].toLocaleString()}/PR
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        );
      })()}


    </Container>
  );
}
// Sending Email Page Component
export const SendingEmailPage: React.FC<{
  onComplete: () => void;
  onBack?: () => void;
}> = ({ onComplete, onBack }) => {
  const [sending, setSending] = useState(true);

  useEffect(() => {
    // Simulate sending emails
    const timer = setTimeout(() => {
      setSending(false);
      setTimeout(() => {
        onComplete();
      }, 2000);
    }, 3000);

    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {/* Navigation Buttons */}
        <StepNavigation onBack={onBack} />
        {sending ? (
          <>
            <CircularProgress size={80} sx={{ color: '#ff5200', mb: 3 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              Sending Your Press Release
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              We're sending your press release to all matched publications. This may take a few moments...
            </Typography>
          </>
        ) : (
          <>
            <CheckCircleIcon sx={{ fontSize: 120, color: '#4caf50', mb: 3 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              Emails Sent Successfully!
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              Your press release has been sent to all matched publications. Redirecting to publication matching...
            </Typography>
          </>
        )}
      </Box>
    </Container>
  );
};

// Payment Status Component
export const PaymentStatus: React.FC<{
  success: boolean;
  onContinue: () => void;
  onRetry?: () => void;
}> = ({ success, onContinue, onRetry }) => {
  const hasProceededRef = useRef(false);
  const onContinueRef = useRef(onContinue);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Update ref when onContinue changes
  useEffect(() => {
    onContinueRef.current = onContinue;
  }, [onContinue]);

  // Auto-proceed on success - only once
  useEffect(() => {
    // Reset hasProceededRef when success becomes true (new payment success)
    if (success) {
      hasProceededRef.current = false;
    }

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (success && !hasProceededRef.current) {
      hasProceededRef.current = true;
      timerRef.current = setTimeout(() => {
        if (onContinueRef.current) {
          try {
            onContinueRef.current();
          } catch (error) {
            console.error('❌ Error calling onContinue:', error);
          }
        }
        timerRef.current = null;
      }, 2000); // Auto-proceed after 2 seconds
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [success]); // Only depend on success, not onContinue

  return (
    <Container maxWidth="md">
      <StepNavigation onBack={onRetry} />
      <Box sx={{ py: 8, textAlign: 'center' }}>
        {success ? (
          <>
            <CheckCircleIcon sx={{ fontSize: 120, color: '#4caf50', mb: 3 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              Payment Successful!
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              Your payment has been processed successfully. Redirecting to distribution...
            </Typography>
            <CircularProgress size={40} sx={{ color: '#ff5200', mb: 3 }} />
            <Button
              variant="contained"
              size="large"
              onClick={onContinue}
              sx={{
                backgroundColor: '#ff5200',
                color: '#fff',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 2,
                textTransform: 'none',
                mt: 2,
                '&:hover': {
                  backgroundColor: '#e64a00',
                },
              }}
            >
              Continue to Distribution
            </Button>
          </>
        ) : (
          <>
            <ErrorOutlineIcon sx={{ fontSize: 120, color: '#f44336', mb: 3 }} />
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              Payment Failed
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
              We couldn't process your payment. Please check your payment details and try again.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              {onRetry && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={onRetry}
                  sx={{
                    borderColor: '#ff5200',
                    color: '#ff5200',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: '#e64a00',
                      backgroundColor: '#fff5f0',
                    },
                  }}
                >
                  Try Again
                </Button>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={onContinue}
                sx={{
                  backgroundColor: '#ff5200',
                  color: '#fff',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#e64a00',
                  },
                }}
              >
                Go Back
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Container>
  );
};

export const RecommendedPublications: React.FC<{
  publications: any[];
  topPublications?: any[];
  onNext: () => void;
  onBack: () => void;
  selectionData?: SelectionData;
  isCorpCrunchSelected?: boolean;
  onCorpCrunchToggle?: (enabled: boolean) => void;
  onCorpCrunchOnly?: () => void;
}> = ({ publications, topPublications, onNext, onBack, selectionData, isCorpCrunchSelected = false, onCorpCrunchToggle, onCorpCrunchOnly }) => {
  const [animatedIndex, setAnimatedIndex] = useState(-1);
  const [allChannels, setAllChannels] = useState<any[]>([]);

  // Constants for styling to match your brand
  const ORIGINAL_ORANGE = '#ff5200';
  const PRIMARY_BLUE = '#1a73e8';
  const DARK_TEXT = '#09273b';

  // Fetch all channels to get full publication details for wishlist items
  useEffect(() => {
    const fetchAllChannels = async () => {
      try {
        const channels = await getChannels();
        if (channels && channels.length > 0) {
          setAllChannels(channels);
        } else {
          console.warn('⚠️ getChannels() returned empty or null');
        }
      } catch (error) {
        console.error('❌ Failed to fetch all channels:', error);
      }
    };
    fetchAllChannels();
  }, []);

  // 1. DATA PREPARATION: Identify and Sort Wishlist Items
  // We extract IDs and ensure they are strings for safe comparison

  // Use topPublications if available (from Step 1), otherwise use publications
  const sourcePublications = topPublications && topPublications.length > 0
    ? topPublications
    : publications;

  // Identify wishlist publication IDs
  const priorityPubIds = (selectionData?.priorityPublications || []).map(id => String(id));

  // Get full publication objects for wishlist items
  // Priority 1: topPublications from Step 1 (should contain selected items)
  // Priority 2: Try allChannels (complete database)
  // Priority 3: Fallback to sourcePublications
  let priorityPubs: any[] = [];
  if (priorityPubIds.length > 0) {
    // PRIORITY 1: Try topPublications first (passed from Step 1 with actual selections)
    if (topPublications && topPublications.length > 0) {
      priorityPubs = priorityPubIds
        .map(id => topPublications.find((pub: any) => String(pub.id) === id || String(pub._id) === id))
        .filter(pub => pub !== undefined);
    }

    // PRIORITY 2: Try allChannels if some are still missing
    if (priorityPubs.length < priorityPubIds.length && allChannels.length > 0) {
      const missingIds = priorityPubIds.filter(id =>
        !priorityPubs.find(pub => String(pub.id) === id || String(pub._id) === id)
      );
      const foundInAll = missingIds
        .map(id => allChannels.find((pub: any) => String(pub.id) === id || String(pub._id) === id))
        .filter(pub => pub !== undefined);
      priorityPubs = [...priorityPubs, ...foundInAll];
    }

    // PRIORITY 3: Final fallback to sourcePublications
    if (priorityPubs.length < priorityPubIds.length) {
      const missingIds = priorityPubIds.filter(id =>
        !priorityPubs.find(pub => String(pub.id) === id || String(pub._id) === id)
      );
      const foundInSource = missingIds
        .map(id => sourcePublications.find((pub: any) => String(pub.id) === id || String(pub._id) === id))
        .filter(pub => pub !== undefined);
      priorityPubs = [...priorityPubs, ...foundInSource];
    }

    if (priorityPubs.length < priorityPubIds.length) {
      const missingIds = priorityPubIds.filter(id =>
        !priorityPubs.find(pub => String(pub.id) === id || String(pub._id) === id)
      );
      console.error('❌ Still missing', missingIds.length, 'publications:', missingIds);
    }
  }

  // Get non-priority publications from AI matches
  // If wishlist exists, we DON'T show AI recommendations - only show wishlist
  const hasWishlist = priorityPubs.length > 0;
  const nonPriorityPubs = hasWishlist
    ? [] // Don't show AI recommendations when wishlist exists
    : sourcePublications.filter((pub: any) => !priorityPubIds.includes(String(pub.id)));

  // Combine: Wishlist items take absolute precedence at the top of the list
  const sortedPublications = [...priorityPubs, ...nonPriorityPubs];

  // Define what goes in the Primary slot and what goes in the Queue
  const primaryPub = sortedPublications[0];

  // Queue shows exactly 5 publications (1 primary + 5 queue = 6 total)
  // If wishlist has publications, show remaining wishlist items (up to 5)
  // Otherwise show AI matches
  const queuePubs = hasWishlist
    ? priorityPubs.filter((pub: any) => pub?.id !== primaryPub?.id).slice(0, 5) // Show up to 5 wishlist items (excluding primary)
    : sortedPublications.slice(1, 6); // Show next 5 AI matches (total 6 with primary)

  const activeIsCorpCrunch = Boolean(isCorpCrunchSelected || selectionData?.tier?.includes('corp-crunch'));

  // Check if priorityPubs is being loaded correctly
  if (priorityPubIds.length > 0 && priorityPubs.length < priorityPubIds.length) {
    console.warn('⚠️ WARNING: Not all wishlist publications were found!');
    console.warn('   Expected:', priorityPubIds.length, 'Got:', priorityPubs.length);
    console.warn('   Missing IDs:', priorityPubIds.filter(id => !priorityPubs.find(p => String(p.id) === id)));
  }

  const handleToggleCorpCrunch = () => {
    if (activeIsCorpCrunch) {
      onCorpCrunchToggle?.(false);
      return;
    }
    onCorpCrunchToggle?.(true);
  };

  useEffect(() => {
    // Staggered animation for the queue list
    const staggeredTimer = setInterval(() => {
      setAnimatedIndex(prev => {
        if (prev >= queuePubs.length - 1) {
          clearInterval(staggeredTimer);
          return prev;
        }
        return prev + 1;
      });
    }, 100);
    return () => clearInterval(staggeredTimer);
  }, [queuePubs.length]);

  // Helper to generate metrics if they aren't provided by the API
  const getExtendedMetrics = (pub: any) => {
    if (!pub) return null;
    const da = pub.domainAuthority || 90;
    const tier = pub.tier || 1;
    const impressions = pub.impressions || (pub.monthlyReach ? Math.round(pub.monthlyReach * 1.5) : (tier === 1 ? 150 : 80));
    const visibility = pub.visibility || (90 + (100 - Math.max(90, da)) / 2);
    const successRate = pub.historicalSuccessRate || (85 + (5 - tier) * 2);

    return {
      daPercent: `${Math.max(90, da)}%`,
      impressions: `${impressions}M+`,
      visibility: `${Math.min(100, Math.round(visibility))}%`,
      successRate: `${Math.min(100, Math.round(successRate))}%`,
      monthlyReach: `${pub.monthlyReach || (tier === 1 ? 100 : 50)}M+`,
      relevance: '98%'
    };
  };

  // 1. Define the Ref for the scrollable container
  const scrollRef = useRef<HTMLDivElement>(null);

  // 2. State for hiding/showing buttons
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScroll, setMaxScroll] = useState(0);

  // 3. Define the scroll function (Fixed naming and logic)
  const handleManualScroll = (scrollOffset: number) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollLeft + scrollOffset,
        behavior: 'smooth',
      });
    }
  };

  // 4. Update scroll positions for the hide/show logic
  const onScrollHandler = () => {
    if (scrollRef.current) {
      setScrollPosition(scrollRef.current.scrollLeft);
      setMaxScroll(scrollRef.current.scrollWidth - scrollRef.current.clientWidth);
    }
  };

  useEffect(() => {
    const node = scrollRef.current;
    if (node) {
      node.addEventListener('scroll', onScrollHandler);
      // Initial calculation
      onScrollHandler();
      return () => node.removeEventListener('scroll', onScrollHandler);
    }
  }, [queuePubs]); // Recalculate if queue items change

  const primaryMetrics = getExtendedMetrics(primaryPub);

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: { xs: 3, md: 5 } }}>

        {/* TOP NAVIGATION */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 5 }}>
          <Button
            onClick={onBack}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            sx={{
              color: DARK_TEXT,
              borderColor: DARK_TEXT,
              borderWidth: '2px',
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 1,
              fontSize: '1rem',
              '&:hover': {
                borderColor: ORIGINAL_ORANGE,
                borderWidth: '2px',
                backgroundColor: 'transparent',
                color: ORIGINAL_ORANGE
              }
            }}
          >
            Back
          </Button>
          <Button
            onClick={onNext}
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: ORIGINAL_ORANGE,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 700,
              px: 4,
              py: 1.5,
              borderRadius: 1,
              fontSize: '1rem',
              boxShadow: '0 4px 12px rgba(255, 82, 0, 0.3)',
              '&:hover': {
                backgroundColor: '#e64a00',
                boxShadow: '0 6px 16px rgba(255, 82, 0, 0.4)'
              }
            }}
          >
            Proceed to checkout
          </Button>
        </Box>

        {/* Corp Crunch Promotion Card - Only show if Corp Crunch is selected */}

        <Box sx={{ mb: 8, textAlign: 'center' }}>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              borderRadius: 4,
              border: activeIsCorpCrunch ? '3px solid #4CAF50' : '1px solid #e0e0e0',
              backgroundColor: activeIsCorpCrunch ? '#f1f8f4' : '#fff',
              maxWidth: '1000px',
              mx: 'auto',
              textAlign: 'left',
              position: 'relative',
              boxShadow: activeIsCorpCrunch ? '0 6px 24px rgba(76, 175, 80, 0.25)' : '0 4px 20px rgba(0,0,0,0.05)',
              transition: 'all 0.3s ease'
            }}
          >
            {/* Selected Badge */}
            {activeIsCorpCrunch && (
              <Box
                sx={{
                  position: 'absolute',
                  top: -15,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  backgroundColor: '#4CAF50',
                  color: '#fff',
                  px: 3,
                  py: 0.8,
                  borderRadius: '20px',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.8,
                  letterSpacing: '0.5px',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.4)'
                }}
              >
                <CheckCircleIcon sx={{ fontSize: '1.2rem' }} />
                CORP CRUNCH SELECTED
              </Box>
            )}
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={8}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      backgroundColor: '#09273b',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <StarIcon sx={{ color: '#fff', fontSize: 32 }} />
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#000' }}>
                        Corp Crunch
                      </Typography>
                      <Chip
                        label="EXCLUSIVE"
                        size="small"
                        sx={{
                          backgroundColor: '#ff5200',
                          color: '#fff',
                          fontWeight: 800,
                          borderRadius: '4px',
                          height: 24,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                    <Typography variant="body2" sx={{ color: '#5f6368', fontWeight: 500 }}>
                      Premium Tier 1 Publication • Skip the queue and publish directly
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={4} sx={{ mb: 3, mt: 1 }}>
                  <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#000' }}>50M+</Typography>
                    <Typography variant="caption" sx={{ color: '#5f6368', fontWeight: 500 }}>Monthly Reach</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#000' }}>95 DA</Typography>
                    <Typography variant="caption" sx={{ color: '#5f6368', fontWeight: 500 }}>Domain Authority</Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#000' }}>24 Hours</Typography>
                    <Typography variant="caption" sx={{ color: '#5f6368', fontWeight: 500 }}>Publish Time</Typography>
                  </Grid>
                </Grid>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<CheckIcon sx={{ fontSize: '16px !important', color: '#4CAF50 !important' }} />}
                    label="Guaranteed Placement"
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: '6px', fontWeight: 600, border: '1px solid #e0e0e0' }}
                  />
                  <Chip
                    icon={<TrendingUpIcon sx={{ fontSize: '16px !important', color: '#ff5200 !important' }} />}
                    label="SEO Optimized"
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: '6px', fontWeight: 600, border: '1px solid #e0e0e0' }}
                  />
                  <Chip
                    icon={<EmailIcon sx={{ fontSize: '16px !important', color: '#2196F3 !important' }} />}
                    label="Direct Contact"
                    variant="outlined"
                    size="small"
                    sx={{ borderRadius: '6px', fontWeight: 600, border: '1px solid #e0e0e0' }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="caption" sx={{ color: '#5f6368', fontWeight: 700, letterSpacing: '0.05em', display: 'block', mb: 1 }}>
                  ONE-TIME PAYMENT
                </Typography>
                <Typography variant="h2" sx={{ fontWeight: 800, color: '#000', mb: 3 }}>
                  $1,499
                </Typography>
                {activeIsCorpCrunch ? (
                  <Button
                    variant="outlined"
                    size="small"
                    endIcon={<CloseIcon sx={{ fontSize: '1rem !important' }} />}
                    onClick={handleToggleCorpCrunch}
                    sx={{
                      backgroundColor: 'transparent',
                      color: '#ff5200',
                      borderColor: '#ff5200',
                      borderWidth: '2px',
                      fontWeight: 700,
                      px: 2.5,
                      py: 1,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '0.9rem',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: '#ff5200',
                        color: '#fff',
                        borderColor: '#ff5200',
                        borderWidth: '2px',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    Deselect Corp Crunch
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Button
                      variant="outlined"
                      size="small"
                      endIcon={<AddIcon sx={{ fontSize: '1rem !important' }} />}
                      onClick={handleToggleCorpCrunch}
                      sx={{
                        backgroundColor: 'transparent',
                        color: '#ff5200',
                        borderColor: '#ff5200',
                        borderWidth: '2px',
                        fontWeight: 700,
                        px: 2.5,
                        py: 1,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease',
                        width: '100%',
                        maxWidth: '280px',
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: '#fff5f0',
                          borderColor: '#e64a00',
                          color: '#e64a00',
                          borderWidth: '2px',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Add Corp Crunch
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      endIcon={<ArrowForwardIcon sx={{ fontSize: '1rem !important' }} />}
                      onClick={onCorpCrunchOnly}
                      sx={{
                        backgroundColor: '#ff5200',
                        color: '#fff',
                        fontWeight: 700,
                        px: 2.5,
                        py: 1,
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 4px 12px rgba(255, 82, 0, 0.3)',
                        width: '100%',
                        maxWidth: '280px',
                        justifyContent: 'center',
                        '&:hover': {
                          backgroundColor: '#e64a00',
                          boxShadow: '0 6px 16px rgba(255, 82, 0, 0.4)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Continue with Corp Crunch
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Paper>
        </Box>


        {/* HEADER SECTION */}
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, color: DARK_TEXT, mb: 1, fontSize: { xs: '2rem', md: '2.8rem' } }}>
            Select Channel and Next in Queue
          </Typography>
          {hasWishlist && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
              <StarIcon sx={{ color: ORIGINAL_ORANGE, fontSize: '1.2rem' }} />
              <Typography variant="body1" sx={{ color: '#666', fontWeight: 500, fontStyle: 'italic' }}>
                Your wishlist publications have been prioritized as primary targets ({priorityPubs.length} publications)
              </Typography>
            </Box>
          )}
        </Box>



        {/* SECTION 1: BEST MATCH (PRIMARY SELECTION) */}
        <Box sx={{ mb: 10 }}>
          <Grid container alignItems="stretch">
            {/* Left Label Box */}
            <Grid item xs={12} md={3.5}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: ORIGINAL_ORANGE, mb: 2 }}>
                {hasWishlist ? 'Your Top Priority' : 'Best match'}
              </Typography>
              <Paper variant="outlined" sx={{
                p: 4, height: '100%', borderRadius: 4,
                border: `2px solid ${ORIGINAL_ORANGE}`, bgcolor: '#fff5f0',
                display: 'flex', flexDirection: 'column', justifyContent: 'center'
              }}>
                <Typography variant="body2" sx={{ color: DARK_TEXT, lineHeight: 1.8, fontWeight: 600 }}>
                  We have mapped {primaryPub?.name} as your top distribution channel.
                  {priorityPubIds.includes(String(primaryPub?.id))
                    ? " This publication was selected from your wishlist."
                    : " This was chosen by our AI for maximum industry impact."}
                </Typography>
              </Paper>
            </Grid>

            {/* Gray Connection Line */}
            <Grid item md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: '100%', height: '2px', bgcolor: '#e0e0e0' }} />
            </Grid>

            {/* Right Primary Selection Card */}
            <Grid item xs={12} md={8}>
              <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                <StarIcon sx={{ color: ORIGINAL_ORANGE }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DARK_TEXT }}>Primary Selection</Typography>
              </Box>

              {primaryPub && (
                <Card
                  sx={{
                    borderRadius: 4,
                    bgcolor: '#fff',
                    color: '#000',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                    position: 'relative',
                    overflow: 'visible',
                    height: '100%'
                  }}>
                  {/* WISH LIST BADGE - Fixed to show only if in wishlist */}
                  {priorityPubIds.includes(String(primaryPub.id)) && (
                    <Box sx={{
                      position: 'absolute', top: -12, left: 24,
                      backgroundColor: ORIGINAL_ORANGE, color: '#fff',
                      px: 2, py: 0.5, borderRadius: '20px', fontWeight: 800, fontSize: '0.7rem', zIndex: 1,
                      display: 'flex', alignItems: 'center', gap: 0.5, boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                    }}>
                      <StarIcon sx={{ fontSize: '0.9rem' }} /> YOUR WISHLIST
                    </Box>
                  )}

                  <Box sx={{
                    position: 'absolute', top: -12, right: 24,
                    backgroundColor: ORIGINAL_ORANGE, color: '#fff',
                    px: 2, py: 0.5, borderRadius: '20px', fontWeight: 800, fontSize: '0.7rem', zIndex: 1,
                    boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                  }}>
                    TOP MATCH
                  </Box>

                  <CardContent sx={{ p: 4 }}>
                    <Grid container spacing={3} alignItems="center">
                      <Grid item xs={12} sm={7}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.5, color: '#000' }}>{primaryPub.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Chip label={`Tier ${primaryPub.tier}`} size="small" sx={{ bgcolor: '#f0f0f0', color: '#000', border: '1px solid #ddd' }} />
                          <Chip label={primaryPub.country || 'Global'} size="small" sx={{ bgcolor: 'transparent', color: '#000', border: '1px solid #ddd' }} />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={5} sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'stretch' }}>
                        {/* DA Score Box */}
                        <Box sx={{
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: '#f5f5f5',
                          borderRadius: 2,
                          flex: 1, // Allows boxes to grow equally
                          maxWidth: 120, // Prevents them from getting too wide
                          border: '1px solid #ddd',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" sx={{ fontWeight: 800, color: '#666', display: 'block', mb: 0.5 }}>
                            DA SCORE
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 900, color: '#000' }}>
                            {primaryMetrics?.daPercent}
                          </Typography>
                        </Box>

                        {/* Content Relevance Score Box */}
                        <Box sx={{
                          textAlign: 'center',
                          p: 1.5,
                          bgcolor: '#f5f5f5',
                          borderRadius: 2,
                          flex: 1,
                          maxWidth: 120,
                          border: '1px solid #ddd',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" sx={{
                            fontWeight: 800,
                            color: '#666',
                            fontSize: '0.6rem',
                            lineHeight: 1.1,
                            mb: 0.5,
                            minHeight: '2.2em',
                            display: 'flex',        // Keep only this one
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center'
                          }}>
                            CONTENT RELEVANCE SCORE
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 900, color: '#4caf50' }}>
                            {primaryMetrics?.relevance}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 3, borderColor: '#e0e0e0' }} />
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <PeopleIcon sx={{ color: '#000 !important', fontSize: '2rem !important', mb: 0.5, display: 'block !important', margin: '0 auto 4px auto', width: '2rem', height: '2rem', visibility: 'visible !important', opacity: 1 }} />
                          <Typography variant="caption" sx={{ display: 'block', color: '#666', fontWeight: 600 }}>Monthly Reach</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#000' }}>{primaryMetrics?.monthlyReach}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <ImpressionsIcon sx={{ color: '#000 !important', fontSize: '2rem !important', mb: 0.5, display: 'block !important', margin: '0 auto 4px auto', width: '2rem', height: '2rem', visibility: 'visible !important', opacity: 1 }} />
                          <Typography variant="caption" sx={{ display: 'block', color: '#666', fontWeight: 600 }}>Impressions</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#000' }}>{primaryMetrics?.impressions}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <VisibilityIcon sx={{ color: '#000 !important', fontSize: '2rem !important', mb: 0.5, display: 'block !important', margin: '0 auto 4px auto', width: '2rem', height: '2rem', visibility: 'visible !important', opacity: 1 }} />
                          <Typography variant="caption" sx={{ display: 'block', color: '#666', fontWeight: 600 }}>Visibility</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#000' }}>{primaryMetrics?.visibility}</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <SuccessRateIcon sx={{ color: '#000 !important', fontSize: '2rem !important', mb: 0.5, display: 'block !important', margin: '0 auto 4px auto', width: '2rem', height: '2rem', visibility: 'visible !important', opacity: 1 }} />
                          <Typography variant="caption" sx={{ display: 'block', color: '#666', fontWeight: 600 }}>Success Rate</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#000' }}>{primaryMetrics?.successRate}</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: ORIGINAL_ORANGE, mb: 2 }}>
            Smart queue
          </Typography>

          <Grid container alignItems="stretch">
            {/* Left Side: Description Card */}
            <Grid item xs={12} md={3.5}>
              <Paper
                variant="outlined"
                sx={{
                  p: 4, height: '100%', borderRadius: 4,
                  border: `2px solid ${ORIGINAL_ORANGE}`, bgcolor: '#fff5f0',
                  display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}
              >
                <Typography variant="body2" sx={{ color: DARK_TEXT, lineHeight: 1.8, fontWeight: 500 }}>
                  Our smart-queue technology handles your outreach by pitching directly to the media channels that matter most to your industry. We work tirelessly until your PR goes live.
                </Typography>
              </Paper>
            </Grid>

            {/* Connecting Line (Desktop Only) */}
            <Grid item md={0.5} sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: '100%', height: '2px', bgcolor: '#e0e0e0' }} />
            </Grid>

            {/* Right Side: Queue Cards with Navigation Overlays */}
            <Grid item xs={12} md={8} sx={{ position: 'relative' }}>
              <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <QueueIcon sx={{ color: ORIGINAL_ORANGE }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DARK_TEXT }}>
                  {hasWishlist ? 'Your Wishlist Queue' : 'Next in Queue'}
                </Typography>
                {hasWishlist && (
                  <Chip
                    label={`${queuePubs.length} Priority Publications`}
                    size="small"
                    sx={{
                      bgcolor: ORIGINAL_ORANGE,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem'
                    }}
                  />
                )}
              </Box>

              {/* CIRCULAR NAV BUTTONS - Positioned on the line height */}
              {scrollPosition > 10 && (
                <IconButton
                  onClick={() => handleManualScroll(-360)}
                  sx={{
                    position: 'absolute', left: -20, top: '50%', zIndex: 10,
                    bgcolor: ORIGINAL_ORANGE, color: 'white',
                    width: 40, height: 40, borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: '#e65100', transform: 'translateY(-50%) scale(1.1)' },
                    transform: 'translateY(-50%)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
              )}

              {scrollPosition < maxScroll - 10 && (
                <IconButton
                  onClick={() => handleManualScroll(360)}
                  sx={{
                    position: 'absolute', right: -10, top: '50%', zIndex: 10,
                    bgcolor: ORIGINAL_ORANGE, color: 'white',
                    width: 40, height: 40, borderRadius: '50%',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                    '&:hover': { bgcolor: '#e65100', transform: 'translateY(-50%) scale(1.1)' },
                    transform: 'translateY(-50%)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              )}

              <Box
                ref={scrollRef}
                sx={{
                  display: 'flex',
                  gap: 0,
                  overflowX: 'auto',
                  pb: 4,
                  px: 2,
                  alignItems: 'center',
                  scrollBehavior: 'smooth',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': { display: 'none' },
                }}
              >
                {queuePubs.map((pub, idx) => {
                  const metrics = getExtendedMetrics(pub);
                  // Safety check: if animatedIndex is not working, default to visible
                  const isVisible = animatedIndex >= idx || animatedIndex === queuePubs.length;
                  const isWishlist = priorityPubIds.includes(String(pub.id));

                  return (
                    <React.Fragment key={idx}>
                      <Paper
                        sx={{
                          p: 2.5, minWidth: 320, borderRadius: 4,
                          border: isWishlist ? `2px solid ${ORIGINAL_ORANGE}` : '1px solid #e0e0e0',
                          opacity: isVisible ? 1 : 0,
                          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                          transition: 'all 0.4s ease',
                          flexShrink: 0,
                          bgcolor: isWishlist ? '#fffcfb' : '#fff',
                          position: 'relative',
                          '&:hover': { borderColor: ORIGINAL_ORANGE, boxShadow: '0 8px 20px rgba(0,0,0,0.06)' },
                        }}
                      >


                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: DARK_TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '70%' }}>
                            {pub.name}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 800, color: ORIGINAL_ORANGE }}>
                            {metrics?.daPercent}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Chip label={`Tier ${pub.tier}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                          <Chip label="96% Rel." size="small" color="success" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700 }} />
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        <Grid container spacing={1}>
                          {[
                            { label: 'Reach', value: metrics?.monthlyReach },
                            { label: 'Impressions', value: metrics?.impressions },
                            { label: 'Visibility', value: metrics?.visibility },
                            { label: 'Success', value: metrics?.successRate, color: '#4caf50' },
                          ].map((m, i) => (
                            <Grid item xs={6} key={i}>
                              <Box sx={{ p: 1, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                                <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase' }}>{m.label}</Typography>
                                <Typography variant="body2" sx={{ fontWeight: 800, color: m.color || DARK_TEXT, fontSize: '0.85rem' }}>{m.value}</Typography>
                              </Box>
                            </Grid>
                          ))}
                        </Grid>
                      </Paper>

                      {/* Horizontal Connector Line */}
                      {idx < queuePubs.length - 1 && (
                        <Box sx={{ minWidth: 50, height: '2px', bgcolor: '#e0e0e0', flexShrink: 0 }} />
                      )}
                    </React.Fragment>
                  );
                })}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* RE-ROUTING INFO BOX */}
        <Box sx={{ maxWidth: '850px', ml: { md: '40%' }, mb: 8 }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: ORIGINAL_ORANGE, mb: 1 }}>
            Re-routing
          </Typography>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: '#fff9f6', borderLeft: `4px solid ${ORIGINAL_ORANGE}` }}>
            <Typography variant="body2" sx={{ color: DARK_TEXT, fontWeight: 600, lineHeight: 1.7 }}>
              Should a channel pass, our smart-queue instantly recalibrates and re-routes your PR to the next optimal match in your wishlist and industry mapping.
            </Typography>
          </Paper>
        </Box>

        {/* BOTTOM CTA */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained" size="large" onClick={onNext}
            endIcon={<ArrowForwardIcon />}
            sx={{
              backgroundColor: ORIGINAL_ORANGE, py: 2, px: 8, fontWeight: 800, fontSize: '1.2rem',
              textTransform: 'none', borderRadius: '50px',
              transition: 'all 0.3s ease',
              '&:hover': { backgroundColor: '#e64a00', transform: 'translateY(-3px)' }
            }}
          >
            Proceed to checkout
          </Button>
        </Box>
      </Box>
    </Container>
  );
};

// Step 4: Checkout Confirmation
export const CheckoutConfirmation: React.FC<{
  publications: any[];
  onConfirm: (success: boolean) => void;
  isCorpCrunch?: boolean;
  onBack?: () => void;
  selectionData?: SelectionData;
  selectedPackage?: PackageData | null;
  prId?: string | null;
  userCredits?: { remaining: number; hasActivePackage: boolean; tierCredits?: { [tier: string]: number } } | null;
}> = ({ publications, onConfirm, isCorpCrunch: initialIsCorpCrunch = false, onBack, selectionData, selectedPackage, prId, userCredits }) => {
  const [activeIsCorpCrunch, setActiveIsCorpCrunch] = useState(initialIsCorpCrunch);
  const summaryRef = useRef<HTMLDivElement>(null);

  const isCorpCrunchExclusive = activeIsCorpCrunch && selectedPackage?.type === 'standalone' && selectedPackage?.tier === 'corp-crunch';

  const corpCrunchPub = {
    id: 'corp-crunch-exclusive',
    name: 'Corp Crunch',
    tier: 1,
    domainAuthority: 95,
    monthlyReach: 50,
    engagementRate: 28,
    topicRelevance: 98,
    country: 'Global',
    email: 'editor@corpcrunch.com',
    isExclusive: true,
    price: 1499
  };

  const activePublications = activeIsCorpCrunch ? [corpCrunchPub] : publications;

  const handleToggleCorpCrunch = () => {
    setActiveIsCorpCrunch(prev => !prev);
    // Smooth scroll to summary section
    setTimeout(() => {
      summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const getTotalPrice = () => {
    // Base price without Corp Crunch add-on
    let basePrice = 0;

    // If package is selected, use package price as-is
    if (selectedPackage && selectedPackage.type !== 'standalone') {
      basePrice = selectedPackage.price;
    } else if (selectedPackage && selectedPackage.type === 'standalone') {
      if (selectedPackage.tier === 'corp-crunch') {
        basePrice = 0; // Corp Crunch only
      } else if (selectionData) {
        // Standalone: calculate based on selectionData tiers, excluding Corp Crunch
        const tiersWithoutCorp = selectionData.tier.filter(t => t !== 'corp-crunch');
        const { totalPrice } = calculateItemPrice(
          'standalone',
          tiersWithoutCorp,
          selectionData.quantity
        );
        basePrice = totalPrice;
      } else {
        // Fallback: calculate based on publications if no selectionData
        basePrice = publications.reduce((sum, pub) => {
          const tier = pub.tier || 4;
          if (tier === 1) return sum + 3999;
          if (tier === 2) return sum + 2999;
          if (tier === 3) return sum + 1999;
          return sum + 999;
        }, 0);
      }
    } else if (selectionData) {
      // Use selection data calculation when no explicit package is selected
      const tiersWithoutCorp = selectionData.tier.filter(t => t !== 'corp-crunch');
      const { totalPrice } = calculateItemPrice(
        selectionData.packageType,
        tiersWithoutCorp,
        selectionData.quantity
      );
      basePrice = totalPrice;
    } else {
      // Last resort fallback
      basePrice = publications.length * 250;
    }

    // If Corp Crunch is active, add its fixed price on top of the base
    const corpCrunchPrice = activeIsCorpCrunch ? 1499 : 0;
    return basePrice + corpCrunchPrice;
  };

  const totalPrice = getTotalPrice();

  // Use the shared, safe Stripe promise created at module level
  const stripePromise = stripeKey && shouldLoadStripe() ? createStripePromise() : null;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6 }}>
        {/* Navigation Buttons */}
        <StepNavigation onBack={onBack} />

        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
            Checkout & Confirm Your Order
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Review your order details before proceeding to payment
          </Typography>
        </Box>



        <Grid container spacing={4} ref={summaryRef}>
          {/* Left Column - Order Details */}
          <Grid item xs={12} md={7}>
            <Paper elevation={0} sx={{ p: 4, backgroundColor: '#f8f9fa', borderRadius: 3, border: '2px solid #e0e0e0', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b', mb: 3 }}>
                Order Details
              </Typography>

              {selectionData && !isCorpCrunchExclusive && (
                <>
                  {/* Package Information */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                      Package Type
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 700, color: '#09273b' }}>
                            {selectedPackage && selectedPackage.type === 'custom'
                              ? `Custom Package (${selectedPackage.credits} PRs)`
                              : selectedPackage && selectedPackage.type === 'package'
                                ? `Package (${selectedPackage.credits} PRs)`
                                : selectionData.packageType === 'standalone'
                                  ? 'Standalone PR'
                                  : 'Package (20 PRs)'}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666' }}>
                            {selectedPackage && selectedPackage.type !== 'standalone'
                              ? 'Valid for 365 days'
                              : selectionData.packageType === 'standalone'
                                ? 'Valid for 30 days'
                                : 'Valid for 365 days'}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff5200' }}>
                          {selectedPackage && selectedPackage.type !== 'standalone'
                            ? `$${selectedPackage.price.toLocaleString()}`
                            : `$${calculateItemPrice(selectionData.packageType, selectionData.tier, selectionData.quantity).totalPrice.toLocaleString()}`}
                        </Typography>
                      </Box>
                    </Paper>
                  </Box>

                  {/* Quantity */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                      {selectedPackage && selectedPackage.type === 'custom' ? 'Total PRs' : 'Quantity'}
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>
                        {selectedPackage && selectedPackage.type === 'custom'
                          ? `${selectedPackage.credits} PRs`
                          : selectedPackage && selectedPackage.type === 'package'
                            ? `${selectedPackage.credits} PRs`
                            : `×${selectionData.quantity}`}
                      </Typography>
                      {selectedPackage && selectedPackage.type === 'custom' && selectedPackage.tierPRCounts && (
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                            Breakdown:
                          </Typography>
                          {Object.entries(selectedPackage.tierPRCounts)
                            .filter(([_, count]) => count > 0)
                            .map(([tier, count]) => (
                              <Typography key={tier} variant="caption" sx={{ color: '#666', display: 'block' }}>
                                Tier {tier}: {count} PR{count !== 1 ? 's' : ''}
                              </Typography>
                            ))}
                        </Box>
                      )}
                    </Paper>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Selection Details */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                      Target Geography ({selectionData.geography.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectionData.geography.map((geo, idx) => (
                        <Chip key={idx} label={geo} size="small" sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0' }} />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                      Industry Clusters ({selectionData.industry.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectionData.industry.map((ind, idx) => (
                        <Chip key={idx} label={ind} size="small" sx={{ backgroundColor: '#fff', border: '1px solid #e0e0e0' }} />
                      ))}
                    </Box>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                      Publication Tiers ({selectionData.tier.length})
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {selectionData.tier.map((tier, idx) => (
                        <Chip key={idx} label={`Tier ${tier}`} size="small" sx={{ backgroundColor: '#09273b', color: '#fff', fontWeight: 600 }} />
                      ))}
                    </Box>
                  </Box>

                  {selectionData.subClusters && selectionData.subClusters.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                        Sub-Clusters ({selectionData.subClusters.length})
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {selectionData.subClusters.map((sub, idx) => (
                          <Chip key={idx} label={sub} size="small" sx={{ backgroundColor: '#fff5f0', color: '#ff5200', border: '1px solid #ff5200' }} />
                        ))}
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {activeIsCorpCrunch && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#09273b', mb: 2 }}>
                    Corp Crunch Exclusive
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Premium Tier 1 publication with guaranteed placement and 24-hour publishing timeline.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label="50M+ Monthly Reach" size="small" sx={{ backgroundColor: '#f0f9ff', color: '#0969da' }} />
                    <Chip label="95 Domain Authority" size="small" sx={{ backgroundColor: '#f0f9ff', color: '#0969da' }} />
                    <Chip label="24h Publish Time" size="small" sx={{ backgroundColor: '#f0f9ff', color: '#0969da' }} />
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b', mb: 2 }}>
                  What happens next?
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <SendIcon sx={{ color: '#ff5200', fontSize: 20 }} />
                    <Typography variant="body2">We send your release to matched channels</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CheckCircleIcon sx={{ color: '#ff5200', fontSize: 20 }} />
                    <Typography variant="body2">Publications review and accept your release</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ScheduleIcon sx={{ color: '#ff5200', fontSize: 20 }} />
                    <Typography variant="body2">Published within 24-28 hours</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <VisibilityIcon sx={{ color: '#ff5200', fontSize: 20 }} />
                    <Typography variant="body2">Track performance in your dashboard</Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, backgroundColor: '#e8f5e9', borderRadius: 2, border: '1px solid #4caf50' }}>
              <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 600 }}>
                ✓ Professional editing included<br />
                ✓ Multi-channel distribution<br />
                ✓ Performance tracking & analytics<br />
                ✓ 30-day money-back guarantee
              </Typography>
            </Paper>
          </Grid>

          {/* Right Column - Payment Summary */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ p: 4, backgroundColor: '#f8f9fa', borderRadius: 3, border: '2px solid #ff5200', position: 'sticky', top: 20 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b', mb: 3 }}>
                Payment Summary
              </Typography>

              {/* User Credits Summary */}
              {userCredits && userCredits.hasActivePackage && (
                <Box sx={{ mb: 3, p: 2, backgroundColor: '#E3F2FD', borderRadius: 2, border: '1px solid #1976D2' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#09273b', mb: 0.5 }}>
                    PR Credits
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#09273b' }}>
                    You have <strong>{userCredits.remaining}</strong> PR credit{userCredits.remaining === 1 ? '' : 's'} remaining.
                  </Typography>
                  {selectedPackage && selectedPackage.type !== 'standalone' && (() => {
                    // Check if tier matches available credits
                    let hasMatchingTierCredits = false;
                    let tierInfo = '';

                    if (selectedPackage.type === 'custom' && selectedPackage.tierPRCounts) {
                      const matchingTiers = Object.entries(selectedPackage.tierPRCounts)
                        .filter(([tier, count]) => {
                          if (count === 0) return false;
                          const tierKey = (tier === '4' || tier === '5') ? '4-5' : tier;
                          const available = userCredits?.tierCredits?.[tierKey] || 0;
                          return available >= count;
                        })
                        .map(([tier]) => tier);
                      hasMatchingTierCredits = matchingTiers.length === Object.entries(selectedPackage.tierPRCounts).filter(([_, count]) => count > 0).length;
                      tierInfo = `Custom package (${Object.entries(selectedPackage.tierPRCounts).filter(([_, c]) => c > 0).map(([t]) => `Tier ${t}`).join(', ')})`;
                    } else if (selectedPackage.tier) {
                      const tierKey = selectedPackage.tier === '4' || selectedPackage.tier === '5' ? '4-5' : selectedPackage.tier;
                      const available = userCredits?.tierCredits?.[tierKey] || 0;
                      hasMatchingTierCredits = available > 0;
                      tierInfo = `Tier ${selectedPackage.tier} package`;
                    }

                    if (hasMatchingTierCredits) {
                      return (
                        <Typography variant="caption" sx={{ color: '#555', display: 'block', mt: 0.5 }}>
                          This order will use <strong>1 credit</strong> from your {tierInfo}.
                        </Typography>
                      );
                    } else {
                      return (
                        <Typography variant="caption" sx={{ color: '#d32f2f', display: 'block', mt: 0.5, fontWeight: 600 }}>
                          ⚠️ No matching tier credits available. This will require payment.
                        </Typography>
                      );
                    }
                  })()}
                  {!selectedPackage && (
                    <Typography variant="caption" sx={{ color: '#555', display: 'block', mt: 0.5 }}>
                      Select a PR package above to pay with your credits. <strong>Standalone plans always require payment.</strong>
                    </Typography>
                  )}
                  {userCredits.tierCredits && Object.keys(userCredits.tierCredits).length > 0 && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #90CAF9' }}>
                      <Typography variant="caption" sx={{ color: '#555', display: 'block', mb: 0.5, fontWeight: 600 }}>
                        Available by tier:
                      </Typography>
                      {Object.entries(userCredits.tierCredits)
                        .filter(([_, count]) => count > 0)
                        .map(([tier, count]) => (
                          <Typography key={tier} variant="caption" sx={{ color: '#555', display: 'block', fontSize: '0.7rem' }}>
                            Tier {tier}: {count} credit{count !== 1 ? 's' : ''}
                          </Typography>
                        ))}
                    </Box>
                  )}
                </Box>
              )}

              {/* Package Information */}
              {selectedPackage && (
                <>
                  <Box sx={{ mb: 2, p: 2, backgroundColor: '#fff5f0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#09273b', mb: 1 }}>
                      Selected Package
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Type: {selectedPackage.type === 'package' ? 'Package' : selectedPackage.type === 'custom' ? 'Custom Package' : 'Standalone'}
                    </Typography>
                    {selectedPackage.tier && (
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Tier: {selectedPackage.tier}
                      </Typography>
                    )}
                    {selectedPackage.customTiers && (
                      <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                        Tiers: {selectedPackage.customTiers.join(', ')}
                      </Typography>
                    )}
                    <Typography variant="body2" sx={{ color: '#666', mb: 0.5 }}>
                      Credits (PRs): {selectedPackage.credits}
                    </Typography>
                    {selectedPackage.type !== 'standalone' && (
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff5200', mt: 1 }}>
                        ${selectedPackage.price.toLocaleString()}
                      </Typography>
                    )}
                    {selectedPackage.type === 'standalone' && selectedPackage.tier === 'corp-crunch' && (
                      <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff5200', mt: 1 }}>
                        ${(1499).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  {/* Custom/Standard Package + Corp Crunch breakdown */}
                  {selectedPackage && selectedPackage.type !== 'standalone' && activeIsCorpCrunch && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                        Price Breakdown
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Package cost
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#09273b' }}>
                          ${selectedPackage.price.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          Corp Crunch add-on
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#09273b' }}>
                          $1,499
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Standalone + Corp Crunch breakdown when applicable */}
                  {selectionData && selectionData.packageType === 'standalone' && !isCorpCrunchExclusive && (
                    (() => {
                      const tiersWithoutCorp = selectionData.tier.filter(t => t !== 'corp-crunch');
                      const { totalPrice: standaloneOnly } = calculateItemPrice(
                        'standalone',
                        tiersWithoutCorp,
                        selectionData.quantity
                      );
                      const corpPrice = activeIsCorpCrunch ? 1499 : 0;
                      return (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ color: '#666', mb: 0.5 }}>
                            Price Breakdown
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                              Standalone tiers
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#09273b' }}>
                              ${standaloneOnly.toLocaleString()}
                            </Typography>
                          </Box>
                          {corpPrice > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" sx={{ color: '#666' }}>
                                Corp Crunch add-on
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#09273b' }}>
                                ${corpPrice.toLocaleString()}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()
                  )}
                </>
              )}

              {selectionData && !isCorpCrunchExclusive && !selectedPackage && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Unit Price:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                      ${calculateItemPrice(selectionData.packageType, selectionData.tier, 1).pricePerUnit.toLocaleString()}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Quantity:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                      ×{selectionData.quantity}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Subtotal:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                  ${totalPrice.toLocaleString()}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Tax:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, color: '#09273b' }}>
                  $0.00
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, backgroundColor: '#fff5f0', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#09273b' }}>
                  Total Amount:
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: '#ff5200' }}>
                  ${totalPrice.toLocaleString()}
                </Typography>
              </Box>

              <CheckoutForm
                totalPrice={totalPrice}
                publications={activePublications}
                isCorpCrunch={activeIsCorpCrunch}
                selectionData={activeIsCorpCrunch ? undefined : selectionData}
                selectedPackage={activeIsCorpCrunch ? null : selectedPackage}
                prId={prId}
                onConfirm={onConfirm}
                stripePromise={stripePromise}
              />
              {stripePromise && (
                <Typography variant="caption" sx={{ color: '#999', display: 'block', textAlign: 'center', mt: 2 }}>
                  Secure payment processing via Stripe
                </Typography>
              )}

              <Divider sx={{ my: 3 }} />

              <Box>
                <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1 }}>
                  Payment secured by Stripe
                </Typography>
                <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                  Your payment information is encrypted and secure. We never store your card details.
                </Typography>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

// Step 4: Sending + Statistical Insights (Combined)
export const SendingWithInsights: React.FC<{
  publications: any[];
  onComplete: (acceptedPub: any) => void;
  prId?: string | null;
  prIds?: string[];
  campaignInfo?: any;
  isFromCampaign?: boolean;
}> = ({ publications, onComplete, prId, prIds, campaignInfo, isFromCampaign }) => {
  const [status, setStatus] = useState<'sending' | 'waiting'>('sending');
  const [currentPublication, setCurrentPublication] = useState<any>(publications[0] || null);
  const [hasSent, setHasSent] = useState(false);

  // Calculate aggregate stats
  const totalReach = publications.reduce((sum, p) => sum + (p.monthlyReach || 0), 0);
  const avgDA = Math.round(publications.reduce((sum, p) => sum + (p.domainAuthority || 0), 0) / publications.length);
  const avgEngagement = Math.round(publications.reduce((sum, p) => sum + (p.engagementRate || 0), 0) / publications.length);

  useEffect(() => {
    if (hasSent || publications.length === 0) return;

    // Send only to first publication
    const sendSingleRequest = async () => {
      try {
        const token = localStorage.getItem('token');
        const apiUrl = getApiBaseUrl();
        const prIdsToSend = (isFromCampaign && prIds && prIds.length > 0) ? prIds : (prId ? [prId] : []);
        const firstPub = publications[0];

        setCurrentPublication(firstPub);
        setStatus('sending');
        setHasSent(true);

        // Show sending animation for 2 seconds
        await new Promise(resolve => setTimeout(resolve, 2000));

        setStatus('waiting');

        // Send request to first publication
        try {
          const apiClient = createApiClient(apiUrl);
          await apiClient.post('/api/distribution/send-request', {
            prId: prId,
            prIds: prIdsToSend,
            publicationEmail: firstPub.email || 'aditya@corpcrunch.io',
            publicationName: firstPub.name,
            publicationId: firstPub.id,
            campaignInfo: campaignInfo || null,
            isCampaign: isFromCampaign || false
          });

          // Show waiting animation for 3 seconds, then redirect
          await new Promise(resolve => setTimeout(resolve, 3000));
        } catch (error) {
          console.error(`Error sending to ${firstPub.name}:`, error);
          // Still wait and redirect even on error
          await new Promise(resolve => setTimeout(resolve, 3000));
        }

        // Clear distribution data and redirect to PR Library
        try {
          localStorage.removeItem('distributionStep');
          localStorage.removeItem('distributionStepLocked');
          localStorage.removeItem('acceptedPublication');
          localStorage.removeItem('prId');
        } catch (e) {
          // Ignore
        }

        // Redirect to PR Library
        window.location.href = '/pr-library';
      } catch (error) {
        console.error('Error sending distribution request:', error);
        // Still redirect after delay
        setTimeout(() => {
          try {
            localStorage.removeItem('distributionStep');
            localStorage.removeItem('distributionStepLocked');
            localStorage.removeItem('acceptedPublication');
            localStorage.removeItem('prId');
          } catch (e) {
            // Ignore
          }
          window.location.href = '/pr-library';
        }, 3000);
      }
    };

    sendSingleRequest();
  }, [hasSent, prId, prIds, publications, isFromCampaign, campaignInfo]);

  const totalCount = publications.length;

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 6 }}>
        <Typography variant="h3" align="center" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
          {status === 'sending' ? 'Sending Request to Publisher' : 'Request Sent'}
        </Typography>
        <Typography variant="body1" align="center" sx={{ color: '#666', mb: 6 }}>
          {currentPublication
            ? status === 'sending'
              ? `Sending your press release to ${currentPublication.name}...`
              : `Your press release has been sent to ${currentPublication.name}`
            : 'Preparing to send...'}
        </Typography>

        <Grid container spacing={4}>
          {/* Left Side - Sending Status */}
          <Grid item xs={12} md={5}>
            <Paper elevation={3} sx={{
              p: 4, textAlign: 'center', borderRadius: 3,
              backgroundColor: '#fff',
              border: '3px solid #ff5200'
            }}>
              <Box sx={{ mb: 3 }}>
                {status === 'sending' && <SendIcon sx={{ fontSize: 70, color: '#ff5200' }} />}
                {status === 'waiting' && <CircularProgress size={70} sx={{ color: '#ff5200' }} />}
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b', mb: 1 }}>
                {currentPublication?.name || 'No Publication'}
              </Typography>
              {currentPublication?.email && (
                <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
                  <EmailIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                  {currentPublication.email}
                </Typography>
              )}

              {status === 'sending' && <Chip label="SENDING REQUEST..." sx={{ backgroundColor: '#ff5200', color: '#fff', fontWeight: 600 }} />}
              {status === 'waiting' && <Chip label="AWAITING RESPONSE..." sx={{ backgroundColor: '#ff9800', color: '#fff', fontWeight: 600 }} />}

              <Box sx={{ mt: 3 }}>
                <LinearProgress
                  variant={status === 'waiting' ? 'indeterminate' : 'determinate'}
                  value={status === 'sending' ? 50 : 75}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: '#e0e0e0', '& .MuiLinearProgress-bar': { backgroundColor: '#ff5200' } }}
                />
                <Typography variant="caption" sx={{ color: '#666', mt: 1, display: 'block' }}>
                  Processing your request...
                </Typography>
              </Box>

              {/* Publishing Timeline Message */}
              <Box sx={{ mt: 3, p: 2, backgroundColor: '#E3F2FD', borderRadius: 2, border: '1px solid #1976D2' }}>
                <ScheduleIcon sx={{ fontSize: 20, color: '#1976D2', mr: 1, verticalAlign: 'middle' }} />
                <Typography variant="body2" sx={{ color: '#1976D2', fontWeight: 600, display: 'inline' }}>
                  Publishing Timeline: 24-48 Hours
                </Typography>
                <Typography variant="caption" sx={{ color: '#1976D2', display: 'block', mt: 0.5 }}>
                  Your press release will be published within 24-48 hours after acceptance
                </Typography>
              </Box>
            </Paper>
          </Grid>

          {/* Right Side - Statistical Insights */}
          <Grid item xs={12} md={7}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#09273b', mb: 3 }}>
              Channel Statistics & Insights
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '2px solid #ff5200' }}>
                  <PeopleIcon sx={{ fontSize: 36, color: '#ff5200', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b' }}>{totalReach}M+</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>Total Audience Reach</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '2px solid #09273b' }}>
                  <BarChartIcon sx={{ fontSize: 36, color: '#09273b', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b' }}>{avgDA}</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>Avg. Domain Authority</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '2px solid #09273b' }}>
                  <TrendingUpIcon sx={{ fontSize: 36, color: '#09273b', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b' }}>{avgEngagement}%</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>Avg. Engagement Rate</Typography>
                </Paper>
              </Grid>
              <Grid item xs={6}>
                <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f8f9fa', borderRadius: 2, border: '2px solid #ff5200' }}>
                  <VisibilityIcon sx={{ fontSize: 36, color: '#ff5200', mb: 1 }} />
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#09273b' }}>{publications.length}</Typography>
                  <Typography variant="body2" sx={{ color: '#666' }}>Matched Publications</Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Publications List */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ color: '#666', mb: 2 }}>Matched Publications:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {publications.map((pub, idx) => {
                  const isCurrent = idx === 0 && currentPublication?.id === pub.id;

                  let statusLabel = 'Pending';
                  let statusColor = '#e0e0e0';

                  if (isCurrent && status === 'sending') {
                    statusLabel = 'Sending...';
                    statusColor = '#ff5200';
                  } else if (isCurrent && status === 'waiting') {
                    statusLabel = 'Processing...';
                    statusColor = '#ff9800';
                  }

                  return (
                    <Chip
                      key={idx}
                      label={pub.name}
                      size="small"
                      sx={{
                        backgroundColor: isCurrent ? statusColor : '#e0e0e0',
                        color: isCurrent ? '#fff' : '#09273b',
                        fontWeight: isCurrent ? 600 : 500,
                        fontSize: '0.7rem'
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Queue Section - Horizontal Queue at Bottom */}
        {publications.length > 1 && (
          <Box sx={{ mt: 8, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2 }}>
              <QueueIcon sx={{ color: '#09273b', fontSize: 24 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>
                Publication Queue
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ textAlign: 'center', color: '#666', mb: 4 }}>
              If the current publication rejects, your PR will automatically move to the next in queue
            </Typography>

            <Box
              sx={{
                display: 'flex',
                gap: 2,
                overflowX: 'auto',
                pb: 3,
                px: 2,
                justifyContent: 'flex-start',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  height: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#e0e0e0',
                  borderRadius: '4px',
                }
              }}
            >
              {publications.map((pub, idx) => {
                const isCurrent = idx === 0 && currentPublication?.id === pub.id;
                const isNext = idx === 1;
                const queuePosition = idx + 1;

                return (
                  <Paper
                    key={pub.id || idx}
                    elevation={0}
                    sx={{
                      p: 2.5,
                      borderRadius: 2,
                      border: isCurrent ? '2px solid #ff5200' : '1px solid #e0e0e0',
                      backgroundColor: '#fff',
                      minWidth: '240px',
                      maxWidth: '260px',
                      flexShrink: 0,
                      position: 'relative'
                    }}
                  >
                    {/* Queue Number */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          backgroundColor: isCurrent ? '#ff5200' : '#f0f0f0',
                          color: isCurrent ? '#fff' : '#666',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: '0.9rem'
                        }}
                      >
                        {queuePosition}
                      </Box>

                      {/* Status Badge */}
                      {isCurrent && (
                        <Chip
                          label={status === 'sending' ? 'Waiting' : 'Waiting'}
                          size="small"
                          sx={{
                            backgroundColor: '#fff5f0',
                            color: '#ff5200',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            height: 24
                          }}
                        />
                      )}

                      {isNext && !isCurrent && (
                        <Chip
                          label="Next"
                          size="small"
                          sx={{
                            backgroundColor: '#fff5f0',
                            color: '#ff5200',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            height: 24
                          }}
                        />
                      )}
                    </Box>

                    {/* Publication Name */}
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 700,
                        color: '#09273b',
                        mb: 1.5,
                        fontSize: '1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {pub.name}
                    </Typography>

                    {/* Chips */}
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 2, flexWrap: 'wrap' }}>
                      <Chip
                        label={`Tier ${pub.tier || 1}`}
                        size="small"
                        sx={{
                          backgroundColor: '#f0f0f0',
                          color: '#09273b',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                      <Chip
                        label={`DA ${pub.domainAuthority || 90}`}
                        size="small"
                        sx={{
                          backgroundColor: '#f0f0f0',
                          color: '#09273b',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 20
                        }}
                      />
                    </Box>

                    {/* Country */}
                    {pub.country && (
                      <Typography variant="caption" sx={{ color: '#666', fontWeight: 500, display: 'block', mb: 1.5 }}>
                        {pub.country}
                      </Typography>
                    )}

                    {/* Metrics */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1.5, borderTop: '1px solid #e0e0e0' }}>
                      <Box>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>
                          Reach
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#09273b', fontWeight: 600, fontSize: '0.85rem' }}>
                          {pub.monthlyReach || 50}M+
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>
                          Success
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600, fontSize: '0.85rem' }}>
                          {pub.historicalSuccessRate || 85}%
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Box>

            {/* Queue Info */}
            <Box sx={{ mt: 4, maxWidth: '700px', mx: 'auto' }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e0e0e0'
                }}
              >
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                  <QueueIcon sx={{ color: '#666', fontSize: 20, mt: 0.25 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#09273b' }}>
                      Automatic Queue System
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: '#666', lineHeight: 1.5 }}>
                      Your PR will automatically move to the next publication if rejected, ensuring your story finds the right home.
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

// Step 5: Final Success
export const PublicationSuccess: React.FC<{ acceptedPub: any }> = ({ acceptedPub }) => {
  // Auto-redirect to analytics after 3 seconds
  useEffect(() => {
    const redirectTimer = setTimeout(() => {
      // Clear all distribution data from localStorage before navigating
      try {
        localStorage.removeItem('distributionStep');
        localStorage.removeItem('distributionStepLocked');
        localStorage.removeItem('acceptedPublication');
        localStorage.removeItem('prId');
        // Reset to step 1 for next distribution
        localStorage.setItem('distributionStep', '1');
      } catch (e) {
        // Ignore
      }
      window.location.href = '/analytics';
    }, 3000); // 3 second delay

    return () => clearTimeout(redirectTimer);
  }, []);

  return (
    <Container maxWidth="md">
      {/* No navigation buttons on success page - auto-redirects to analytics */}
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 120, color: '#4caf50', mb: 3 }} />

        <Typography variant="h3" sx={{ fontWeight: 700, color: '#09273b', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
          Press Release Accepted!
        </Typography>

        <Typography variant="h5" sx={{ color: '#666', mb: 4 }}>
          Your release has been sent to <strong style={{ color: '#ff5200' }}>{acceptedPub?.name}</strong>
        </Typography>

        <Paper elevation={0} sx={{ p: 4, backgroundColor: '#e8f5e9', borderRadius: 3, border: '2px solid #4caf50', mb: 4 }}>
          <ScheduleIcon sx={{ fontSize: 50, color: '#4caf50', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#2e7d32', mb: 1 }}>
            Publishing Timeline: 24-48 Hours
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Your press release will be published on {acceptedPub?.name}
          </Typography>
        </Paper>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>{acceptedPub?.domainAuthority}</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Domain Authority</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>{acceptedPub?.monthlyReach}M</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Monthly Reach</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>{acceptedPub?.engagementRate}%</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Engagement</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper elevation={0} sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#09273b' }}>Tier {acceptedPub?.tier}</Typography>
              <Typography variant="caption" sx={{ color: '#666' }}>Publication Tier</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
          You will receive an email notification when your article is live.
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mb: 4 }}>
          Redirecting to Analytics Dashboard in 3 seconds...
        </Typography>
        <CircularProgress size={40} sx={{ color: '#ff5200', mb: 3 }} />
      </Box>
    </Container>
  );
};

// Main Distribution Interface
export const DistributionInterface: React.FC = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const prIdFromUrl = searchParams.get('prId');
  const prIdsFromUrl = searchParams.get('prIds'); // Multiple PR IDs
  const campaignIdFromUrl = searchParams.get('campaignId');
  const campaignNameFromUrl = searchParams.get('campaignName');
  const startDateFromUrl = searchParams.get('startDate');
  const endDateFromUrl = searchParams.get('endDate');

  const [prId, setPrId] = useState<string | null>(prIdFromUrl || null);
  const [prIds, setPrIds] = useState<string[]>([]);
  const [availablePRs, setAvailablePRs] = useState<any[]>([]);
  const [selectedPRsForDistribution, setSelectedPRsForDistribution] = useState<string[]>([]);
  const [campaignInfo, setCampaignInfo] = useState<any>(null);
  const [isFromCampaign, setIsFromCampaign] = useState(false); // Track if coming from campaign
  const [currentPRIndex, setCurrentPRIndex] = useState(0);

  // Always start from step 1 - clear any previous step state
  const getInitialStep = () => {
    try {
      // Clear any previous distribution step state to ensure fresh start
      localStorage.removeItem('distributionStep');
      localStorage.removeItem('distributionStepLocked');
      localStorage.removeItem('acceptedPublication');
    } catch (e) {
      // Ignore localStorage errors
    }
    return 1;
  };

  const [step, setStep] = useState(getInitialStep()); // Start at step 1, will be set to 0 if multiple PRs need selection from campaign
  const [selectionData, setSelectionData] = useState<SelectionData | undefined>(undefined);
  const [selectedPackage, setSelectedPackage] = useState<PackageData | null>(null);
  const [foundPubs, setFoundPubs] = useState<any[]>([]);
  const [topPublications, setTopPublications] = useState<any[]>([]);
  // acceptedPub is no longer used since we removed step 7 (PublicationSuccess page)
  // Keeping for backward compatibility but not initializing from localStorage
  const [acceptedPub, setAcceptedPub] = useState<any>(null);
  const [isCorpCrunch, setIsCorpCrunch] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userCredits, setUserCredits] = useState<{
    remaining: number;
    hasActivePackage: boolean;
    tierCredits?: { [tier: string]: number };
  } | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<boolean | null>(null); // null = not processed, true = success, false = failed
  const initializedRef = useRef(false); // Track if component has been initialized
  const paymentStatusRef = useRef<boolean | null>(null); // Track payment status in ref for callbacks
  const stepRef = useRef<number>(1); // Track step in ref to prevent resets
  const isNavigatingBackRef = useRef<boolean>(false); // Track if we're navigating back to prevent auto-advancement

  // Create axios instance for API calls - memoize to avoid recreating on every render
  const apiClient = React.useMemo(() => createApiClient(getApiBaseUrl()), []);

  // Fetch PR details
  const fetchPRDetails = async (prIdList: string[]) => {
    try {
      // Remove duplicates from prIdList
      const uniquePrIds = Array.from(new Set(prIdList.map(id => id.trim()).filter(id => id)));

      const prs = await Promise.all(
        uniquePrIds.map(async (id) => {
          try {
            const response = await apiClient.get(`/api/pr/${id}`);
            const data = response.data;
            const pressRelease = data.pressRelease || data;
            // Ensure we have a consistent ID format
            return {
              ...pressRelease,
              _id: pressRelease._id || pressRelease.id || id,
              id: pressRelease.id || pressRelease._id || id,
              title: pressRelease.title || `Press Release ${id.substring(0, 8)}...`
            };
          } catch (err: any) {
            console.error(`Error fetching PR ${id}:`, err);
            // If it's a 401, the interceptor will handle logout/redirect
            // Return a fallback object so the component can still render
            return {
              _id: id,
              id: id,
              title: `Press Release ${id.substring(0, 8)}...`,
              status: 'draft'
            };
          }
        })
      );

      // Remove duplicates based on ID
      const uniquePRs = prs.filter((pr, index, self) =>
        index === self.findIndex((p) => (p._id || p.id) === (pr._id || pr.id))
      );

      setAvailablePRs(uniquePRs);
      // Select all by default (using the unique IDs)
      setSelectedPRsForDistribution(uniquePrIds);
    } catch (err) {
      console.error('Error fetching PR details:', err);
      setError('Failed to load press release details. Please try again.');
    }
  };

  // Fetch available PRs for selection when no PR ID is provided
  const fetchAvailablePRsForSelection = async () => {
    try {
      const prs = await getPressReleases();
      // Filter to only show submitted/verified/published PRs (exclude drafts)
      const distributablePRs = prs.filter(pr =>
        pr.status === 'submitted' ||
        pr.status === 'verified' ||
        pr.status === 'published' ||
        pr.status === 'distributed'
      );
      setAvailablePRs(distributablePRs);
      // Auto-select first PR if available
      if (distributablePRs.length > 0 && !prId) {
        const firstPR = distributablePRs[0];
        setPrId(firstPR._id);
        setPrIds([firstPR._id]);
        localStorage.setItem('prId', firstPR._id);
      }
      return distributablePRs;
    } catch (err) {
      console.error('Error fetching available PRs:', err);
      return [];
    }
  };

  // Handle multiple PRs and campaign info - only run once on mount
  useEffect(() => {
    // Only initialize once - don't reset if already initialized
    // Also don't reset if we're past step 3 (in the payment/distribution flow)
    if (initializedRef.current || stepRef.current > 3) {
      if (!initializedRef.current && stepRef.current > 3) {
        setLoading(false);
      }
      return;
    }

    initializedRef.current = true;
    let fromCampaign = false;

    // Safety timeout: ensure loading is set to false after 10 seconds max
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ DistributionInterface: Loading timeout reached, setting loading to false');
      setLoading(false);
    }, 10000);

    // Helper function to clear timeout and set loading to false
    const finishLoading = () => {
      clearTimeout(loadingTimeout);
      setLoading(false);
    };

    // Check for campaign data in localStorage first (indicates coming from campaign creation)
    try {
      const storedCampaignData = localStorage.getItem('campaignDistributionData');
      if (storedCampaignData) {
        const data = JSON.parse(storedCampaignData);
        if (data.prIds && data.prIds.length > 0) {
          setPrIds(data.prIds);
          setPrId(data.prIds[0]); // Set first PR as active
          setCampaignInfo(data.campaignDetails);
          setIsFromCampaign(true);
          fromCampaign = true;
          localStorage.setItem('prId', data.prIds[0]);
          // Fetch PR details and set step to 0 if multiple PRs from campaign
          if (data.prIds.length > 1) {
            fetchPRDetails(data.prIds).catch((err) => {
              console.error('Error fetching PR details from campaign:', err);
              // Continue anyway - component can still render
            }).finally(() => {
              finishLoading();
            });
            setStep(0);
          } else if (data.prIds.length === 1) {
            // Single PR from campaign - fetch details but go directly to step 1
            fetchPRDetails(data.prIds).catch((err) => {
              console.error('Error fetching PR details from campaign:', err);
              // Continue anyway - component can still render
            }).finally(() => {
              finishLoading();
            });
            setSelectedPRsForDistribution(data.prIds);
            setStep(1);
          } else {
            finishLoading();
          }
          // Clear the campaign data from localStorage after reading
          localStorage.removeItem('campaignDistributionData');
          return; // Exit early since we handled localStorage data
        }
      }
    } catch (err) {
      console.error('Error reading campaign data from localStorage:', err);
    }

    // Handle URL parameters - check if campaignId is present (indicates from campaign)
    if (campaignIdFromUrl || campaignNameFromUrl) {
      fromCampaign = true;
      setIsFromCampaign(true);
      setCampaignInfo({
        id: campaignIdFromUrl,
        name: campaignNameFromUrl ? decodeURIComponent(campaignNameFromUrl) : null,
        startDate: startDateFromUrl ? decodeURIComponent(startDateFromUrl) : null,
        endDate: endDateFromUrl ? decodeURIComponent(endDateFromUrl) : null
      });
    }

    // Handle URL parameters for PRs
    if (prIdsFromUrl) {
      const prIdsArray = prIdsFromUrl.split(',').filter(id => id.trim());
      if (prIdsArray.length > 0) {
        setPrIds(prIdsArray);
        setPrId(prIdsArray[0]);
        localStorage.setItem('prId', prIdsArray[0]);
        // Only show PR selection step if coming from campaign AND multiple PRs
        if (fromCampaign && prIdsArray.length > 1) {
          fetchPRDetails(prIdsArray).catch((err) => {
            console.error('Error fetching PR details from URL:', err);
            // Continue anyway - component can still render
          }).finally(() => {
            finishLoading();
          });
          setStep(0);
        } else if (fromCampaign && prIdsArray.length === 1) {
          // Single PR from campaign - fetch details but go directly to step 1
          fetchPRDetails(prIdsArray).catch((err) => {
            console.error('Error fetching PR details from URL:', err);
            // Continue anyway - component can still render
          }).finally(() => {
            finishLoading();
          });
          setSelectedPRsForDistribution(prIdsArray);
          setStep(1);
        } else {
          finishLoading();
        }
      } else {
        setError('No valid PR IDs found in URL');
        finishLoading();
      }
    } else if (prIdFromUrl) {
      if (prIdFromUrl.trim()) {
        setPrId(prIdFromUrl);
        setPrIds([prIdFromUrl]);
        try {
          localStorage.setItem('prId', prIdFromUrl);
        } catch {
          // ignore storage errors
        }
        finishLoading();
      } else {
        setError('Invalid PR ID in URL');
        finishLoading();
      }
    } else {
      // If not present in URL, try restoring from localStorage
      try {
        const stored = localStorage.getItem('prId');
        if (stored && stored.trim()) {
          setPrId(stored);
          setPrIds([stored]);
          finishLoading();
        } else {
          // No PR ID found - fetch available PRs and auto-select first one
          // Then show main distribution UI directly
          fetchAvailablePRsForSelection().catch((err) => {
            console.error('Error fetching available PRs:', err);
            // Don't set error state - just show empty state
          }).finally(() => {
            finishLoading();
          });
        }
      } catch {
        // On error, proceed to main UI anyway - PR can be selected later
        finishLoading();
      }
    }

    // Cleanup timeout on unmount
    return () => {
      clearTimeout(loadingTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Reset to step 1 on component mount - ensure fresh start every time
  useEffect(() => {
    // Clear any previous distribution state
    try {
      localStorage.removeItem('distributionStep');
      localStorage.removeItem('distributionStepLocked');
      localStorage.removeItem('acceptedPublication');
    } catch (e) {
      // Ignore localStorage errors
    }
    // Clear all state to ensure fresh start
    setFoundPubs([]);
    isNavigatingBackRef.current = false;
    // Reset step to 1
    setStep(1);
    stepRef.current = 1;
  }, []); // Run only once on mount

  // Fetch user credits on component mount
  useEffect(() => {
    const fetchUserCredits = async () => {
      try {
        const token = getToken();
        if (!token) {
          setUserCredits({ remaining: 0, hasActivePackage: false, tierCredits: {} });
          return;
        }

        const response = await apiClient.get('/api/payments/credits');
        const data = response.data;
        setUserCredits({
          remaining: data.credits?.remaining || 0,
          hasActivePackage: data.hasActivePackage || false,
          tierCredits: data.tierCredits || {}
        });
      } catch (error: any) {
        console.error('Error fetching user credits:', error);
        // If it's a 401, the interceptor will handle logout/redirect
        // But don't prevent component from rendering - set default credits
        setUserCredits({ remaining: 0, hasActivePackage: false, tierCredits: {} });
      }
    };

    // Only fetch credits if component is not in loading state
    if (!loading) {
      fetchUserCredits();
    }
  }, [apiClient, loading]);

  // Update step ref (but don't persist to localStorage - always start fresh on page load)
  useEffect(() => {
    stepRef.current = step;
    // Reset navigation back flag when step changes to something other than 3
    // (The flag will be checked and reset in step 3's onComplete handler)
    if (step !== 3 && step !== 4) {
      isNavigatingBackRef.current = false;
    }
    // If we're on step 4 but don't have publications, go back to step 1
    if (step === 4 && foundPubs.length === 0) {
      console.warn('⚠️ Step 4 reached without publications, redirecting to step 1');
      setFoundPubs([]);
      isNavigatingBackRef.current = false;
      setStep(1);
    }
  }, [step, paymentStatus, foundPubs.length]);

  const handleCorpCrunchSelect = () => {
    // Don't skip package selection page - let user see Corp Crunch card highlighted
    // The isCorpCrunch flag will be used to highlight the Corp Crunch card in PackageSelection
    setIsCorpCrunch(true);
    // Step 2 is the package selection page - don't skip it
    // User will see Corp Crunch and Standalone cards highlighted
  };

  const handleBackToSelection = () => {
    // Only allow going back if we're not past payment step
    if (step <= 4) {
      // Clear publications and reset flags to prevent auto-advancement
      setFoundPubs([]);
      isNavigatingBackRef.current = false;
      // Go back to step 1, preserving selectionData so users can modify their selections
      setStep(1);
    }
  };

  const handleSelectionComplete = (data: SelectionData, tops?: any[]) => {
    // data already contains priorityPublications from GeographyChannelMapping
    setSelectionData(data);
    if (tops && tops.length > 0) {
      setTopPublications(tops);
    }
    setIsCorpCrunch(false);
    setStep(2);
  };



  const handlePackageSelect = (packageData: PackageData, includeCorpCrunch: boolean) => {
    setSelectedPackage(packageData);

    // Sync Corp Crunch add-on with selection data
    if (selectionData) {
      const hasCorp = selectionData.tier.includes('corp-crunch');
      let updatedTiers = selectionData.tier;

      if (includeCorpCrunch && !hasCorp) {
        updatedTiers = [...selectionData.tier, 'corp-crunch'];
      } else if (!includeCorpCrunch && hasCorp) {
        updatedTiers = selectionData.tier.filter(t => t !== 'corp-crunch');
      }

      setSelectionData({
        ...selectionData,
        tier: updatedTiers
      });
    }

    setIsCorpCrunch(includeCorpCrunch);

    // If package is selected (not standalone), skip checkout and go directly to arc animation
    setStep(3); // Go to arc animation
  };

  const handleCorpCrunchCheckoutToggle = (enabled: boolean) => {
    setIsCorpCrunch(enabled);

    // Sync selectionData.tier as well to reflect add-on status
    if (selectionData) {
      setSelectionData(prev => {
        if (!prev) return prev;
        const currentTiers = prev.tier;
        const hasCorp = currentTiers.includes('corp-crunch');
        if (enabled && !hasCorp) {
          return { ...prev, tier: [...currentTiers, 'corp-crunch'] };
        } else if (!enabled && hasCorp) {
          return { ...prev, tier: currentTiers.filter(t => t !== 'corp-crunch') };
        }
        return prev;
      });
    }

    if (!enabled) {
      // If user deselects from banner, only wipe the package if it was EXCLUSIVELY corp-crunch.
      setSelectedPackage(prev =>
        prev?.type === 'standalone' && prev?.tier === 'corp-crunch'
          ? {
            type: 'standalone',
            price: 0,
            credits: 0
          }
          : prev
      );
    }
  };

  const handleCorpCrunchOnly = () => {
    setIsCorpCrunch(true);
    setSelectedPackage({
      type: 'standalone',
      tier: 'corp-crunch',
      price: 1499,
      credits: 1
    });
    setStep(5); // Proceed directly to checkout
  };

  const handlePackageBack = () => {
    // Clear publications and reset flags to prevent auto-advancement
    setFoundPubs([]);
    isNavigatingBackRef.current = false;
    setStep(1); // Go back to geography selection
  };

  const handleTogglePRSelection = (prId: string) => {
    const normalizedPrId = String(prId).trim();
    setSelectedPRsForDistribution(prev => {
      const normalizedPrev = prev.map(id => String(id).trim());
      return normalizedPrev.includes(normalizedPrId)
        ? prev.filter(id => String(id).trim() !== normalizedPrId)
        : [...prev, normalizedPrId];
    });
  };

  const handleSelectAllPRs = () => {
    if (selectedPRsForDistribution.length === prIds.length) {
      setSelectedPRsForDistribution([]);
    } else {
      setSelectedPRsForDistribution([...prIds]);
    }
  };

  const handlePRSelectionComplete = () => {
    if (selectedPRsForDistribution.length === 0) {
      alert('Please select at least one PR to distribute');
      return;
    }
    // Update prIds to only selected ones
    setPrIds(selectedPRsForDistribution);
    setPrId(selectedPRsForDistribution[0]);
    localStorage.setItem('prId', selectedPRsForDistribution[0]);
    setStep(1);
  };

  // Show loading or error state
  // Safety: Always show UI after max 10 seconds (handled by timeout in useEffect)
  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: theme.palette.text.primary }}>
            Loading distribution page...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
            If this takes too long, please refresh the page
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          onClick={() => window.location.href = '/create-pr'}
        >
          Create New Press Release
        </Button>
      </Container>
    );
  }

  // If no PR ID but we have available PRs, auto-select the first one and proceed
  // Otherwise, show the main distribution UI directly (step 1)
  // The PR selection can happen later in the flow if needed

  return (
    <>
      <GlobalStyles styles={{
        'html, body': {
          overflowX: 'hidden',
          width: '100%',
          margin: 0,
          padding: 0
        },
        '#root': {
          overflowX: 'hidden',
          width: '100%'
        }
      }} />
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        py: 4,
        overflowX: 'hidden',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        '& .MuiPaper-root': {
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,
        },
        '& .MuiCard-root': {
          color: theme.palette.text.primary,
        },
        '& .MuiTypography-root': {
          color: theme.palette.text.primary,
        },
        '& .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6': {
          color: theme.palette.text.primary,
        },
        '& .MuiTypography-body2, & .MuiTypography-caption': {
          color: theme.palette.text.secondary,
        },
        '& .MuiBox-root': {
          borderColor: theme.palette.divider,
        },
        '& .MuiChip-root': {
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.2)' : undefined,
          color: theme.palette.mode === 'dark' ? theme.palette.text.primary : undefined,
        },
      }}>

        {/* Step 0: PR Selection (only if coming from campaign with multiple PRs) */}
        {step === 0 && prIds.length > 1 && isFromCampaign && (
          <Container maxWidth="lg">
            <Paper elevation={3} style={{ padding: '32px', marginBottom: '24px' }}>
              <Typography variant="h4" style={{ fontWeight: 'bold', marginBottom: '8px', color: theme.palette.text.primary }}>
                Select Press Releases to Distribute
              </Typography>
              <Typography variant="body1" style={{ marginBottom: '24px', color: theme.palette.text.secondary }}>
                Choose which press releases from your campaign you want to distribute. You can select all or choose specific ones.
              </Typography>

              <Box style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '24px',
                padding: '16px',
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
                borderRadius: '8px'
              }}>
                <Typography variant="body1" style={{ fontWeight: 'bold', color: theme.palette.text.primary }}>
                  {selectedPRsForDistribution.length} of {prIds.length} PR{prIds.length !== 1 ? 's' : ''} selected
                </Typography>
                <Button
                  variant={selectedPRsForDistribution.length === prIds.length ? "contained" : "outlined"}
                  onClick={handleSelectAllPRs}
                  size="medium"
                >
                  {selectedPRsForDistribution.length === prIds.length ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>

              <Grid container spacing={2}>
                {availablePRs
                  .filter((pr, index, self) => {
                    // Remove duplicates based on ID
                    const prId = pr._id || pr.id;
                    return index === self.findIndex((p) => (p._id || p.id) === prId);
                  })
                  .map((pr) => {
                    const prIdValue = String(pr._id || pr.id).trim();
                    const isSelected = selectedPRsForDistribution.some(
                      selectedId => String(selectedId).trim() === prIdValue
                    );
                    return (
                      <Grid item xs={12} sm={6} md={4} key={prIdValue}>
                        <Card
                          onClick={() => handleTogglePRSelection(prIdValue)}
                          style={{
                            cursor: 'pointer',
                            border: isSelected
                              ? `2px solid ${theme.palette.primary.main}`
                              : `2px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
                            backgroundColor: isSelected
                              ? (theme.palette.mode === 'dark' ? 'rgba(58, 79, 237, 0.2)' : 'rgba(9, 39, 235, 0.08)')
                              : theme.palette.background.paper,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            height: '100%'
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = theme.palette.primary.main;
                              e.currentTarget.style.transform = 'translateY(-2px)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)';
                              e.currentTarget.style.transform = 'translateY(0)';
                            }
                          }}
                        >
                          <CardContent style={{ padding: '16px' }}>
                            <Box style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                              <Checkbox
                                checked={isSelected}
                                style={{
                                  padding: 0,
                                  marginRight: '8px'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTogglePRSelection(prIdValue);
                                }}
                              />
                              <ArticleIcon
                                style={{
                                  fontSize: '32px',
                                  color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary
                                }}
                              />
                            </Box>
                            <Typography
                              variant="subtitle1"
                              style={{
                                fontWeight: 'bold',
                                color: theme.palette.text.primary,
                                marginBottom: '8px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}
                            >
                              {pr.title || `Press Release ${prIdValue.substring(0, 8)}...`}
                            </Typography>
                            <Chip
                              label={pr.status || 'draft'}
                              size="small"
                              style={{
                                backgroundColor: pr.status === 'published' ? '#4CAF50' :
                                  pr.status === 'draft' ? '#9E9E9E' :
                                    theme.palette.primary.main,
                                color: 'white',
                                fontSize: '0.7rem'
                              }}
                            />
                            {isSelected && (
                              <Box style={{
                                position: 'absolute',
                                top: '8px',
                                right: '8px',
                                backgroundColor: theme.palette.primary.main,
                                borderRadius: '50%',
                                width: '24px',
                                height: '24px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                                <CheckCircleIcon style={{ color: 'white', fontSize: '16px' }} />
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
              </Grid>

              <Box style={{ marginTop: '32px', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    // Go back or cancel
                    window.history.back();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handlePRSelectionComplete}
                  disabled={selectedPRsForDistribution.length === 0}
                  size="large"
                >
                  Continue with {selectedPRsForDistribution.length} Selected PR{selectedPRsForDistribution.length !== 1 ? 's' : ''}
                </Button>
              </Box>
            </Paper>
          </Container>
        )}

        {step === 1 && (
          <GeographyChannelMapping
            onComplete={(data, tops) => handleSelectionComplete(data, tops)}
            onCorpCrunchSelect={handleCorpCrunchSelect}
            initialCriteria={selectionData}
            prId={prId}
          />
        )}
        {step === 2 && <PackageSelection
          onSelect={handlePackageSelect}
          onBack={handlePackageBack}
          selectionData={selectionData}
        />}
        {step === 3 && <RealTimePublicationScanning
          searchCriteria={selectionData}
          prId={prId}
          onComplete={async (pubs) => {
            // Don't auto-advance if we're navigating back
            if (isNavigatingBackRef.current) {
              isNavigatingBackRef.current = false; // Reset the flag
              setFoundPubs(pubs); // Update publications but don't advance
              return;
            }
            // Only advance if we're still on step 3 (prevent race conditions)
            if (stepRef.current !== 3) {
              setFoundPubs(pubs); // Update publications but don't advance
              return;
            }
            setFoundPubs(pubs);
            // Always go to recommended publications page first
            setStep(4);
          }}
          onBack={() => {
            isNavigatingBackRef.current = false; // Reset flag when going back
            setStep(2);
          }}
        />}
        {step === 4 && foundPubs.length > 0 && <RecommendedPublications
          publications={foundPubs}
          topPublications={topPublications}
          selectionData={selectionData}
          isCorpCrunchSelected={isCorpCrunch}
          onCorpCrunchToggle={handleCorpCrunchCheckoutToggle}
          onCorpCrunchOnly={handleCorpCrunchOnly}
          onNext={() => {
            // Check if we should skip checkout:
            // 1. Package must be selected (not standalone) - standalone NEVER uses credits
            // 2. User must have purchased a package (hasActivePackage = true)
            // 3. User must have remaining credits for the SELECTED tier (> 0)
            // 4. Selected package tier must MATCH available tier credits
            let shouldSkipCheckout = false;

            if (selectedPackage && selectedPackage.type !== 'standalone' && userCredits?.hasActivePackage === true) {
              if (selectedPackage.type === 'custom' && selectedPackage.tierPRCounts) {
                // Custom package: check if we have credits for each tier in the custom package
                const hasAllTierCredits = Object.entries(selectedPackage.tierPRCounts).every(([tier, count]) => {
                  if (count === 0) return true; // Skip tiers with 0 PRs
                  const tierKey = (tier === '4' || tier === '5') ? '4-5' : tier;
                  const available = userCredits?.tierCredits?.[tierKey] || 0;
                  return available >= count;
                });
                shouldSkipCheckout = hasAllTierCredits;
              } else if (selectedPackage.tier) {
                // Standard package: check if tier matches available credits
                const tierKey = selectedPackage.tier === '4' || selectedPackage.tier === '5' ? '4-5' : selectedPackage.tier;
                const availableCredits = userCredits?.tierCredits?.[tierKey] || 0;
                // Only use credits if we have at least 1 credit for this tier
                shouldSkipCheckout = availableCredits > 0;
              }
            }

            if (shouldSkipCheckout) {
              setPaymentStatus(true); // Mark as paid (using credits)
              paymentStatusRef.current = true; // Update ref
              setStep(7); // Skip checkout, go directly to sending publications (user has credits)
            } else {
              setStep(5); // Go to checkout (for standalone or if no credits remaining)
            }
          }}
          onBack={() => {
            // Set flag to prevent auto-advancement when going back to step 3
            isNavigatingBackRef.current = true;
            setStep(3);
          }}
        />}
        {step === 5 && <CheckoutConfirmation
          publications={foundPubs}
          onConfirm={(success) => {
            setPaymentStatus(success);
            paymentStatusRef.current = success; // Update ref
            setStep(6); // Go to payment status page
          }}
          isCorpCrunch={isCorpCrunch}
          onBack={() => setStep(4)}
          selectionData={selectionData}
          selectedPackage={selectedPackage}
          prId={prId}
          userCredits={userCredits}
        />}
        {step === 6 && paymentStatus !== null && (
          <PaymentStatus
            key={`payment-status-${paymentStatus}`} // Force remount when paymentStatus changes
            success={paymentStatus}
            onContinue={() => {
              const currentPaymentStatus = paymentStatusRef.current ?? paymentStatus;

              // Use the ref value to ensure we have the latest payment status
              if (currentPaymentStatus === true) {
                setStep(7); // Go directly to sending publications (skip email page)
              } else if (currentPaymentStatus === false) {
                setStep(5); // Go back to checkout
              } else {
                console.warn('⚠️ Payment status is null, defaulting to step 5');
                setStep(5);
              }
            }}
            onRetry={paymentStatus === false ? () => {
              setStep(5);
            } : undefined}
          />
        )}
        {step === 7 && <SendingWithInsights
          publications={foundPubs}
          onComplete={() => {
            // This callback is no longer used since we redirect directly from SendingWithInsights
            // Keeping for backward compatibility
          }}
          prId={prId}
          prIds={isFromCampaign && selectedPRsForDistribution.length > 0 ? selectedPRsForDistribution : (prId ? [prId] : [])}
          campaignInfo={campaignInfo}
          isFromCampaign={isFromCampaign}
        />}
      </Box>
    </>
  );
};
